/*
  bf-training-screens.js
  One function per prototype route.

  Each screen follows the package's layout (page head, split, panels, rows) and
  fills it from the backend. Where a value does not exist yet the screen says so
  in place rather than showing a plausible number — a member reading "0 of 3"
  should be able to trust it.
*/
(function () {
  'use strict';
  var A = window.BFTrainingApp;
  if (!A) return;
  var H = A.helpers;
  var esc = H.esc, btn = H.btn, tag = H.tag, panel = H.panel, row = H.row,
      head = H.head, note = H.note, empty = H.empty, money = H.money,
      api = H.api, load = H.load, S = H.state, CFG = H.config;

  /* The member chose equipment that matches no approved station in this
     session. The server says so explicitly (noCompatiblePlan) instead of
     sending an empty list, and it never widens back to every station — a
     member who said "bodyweight" must not be handed machine work. Every
     screen that lists stations uses this instead of a generic "nothing yet",
     so the member learns the cause and what would fix it. */
  /* Exposed on the app object so the second screens file uses the same
     wording — one message, not two that can drift apart. */
  /* WHO CHECKED THIS, SAID THE SAME WAY EVERYWHERE.
     Of the 64 stations in the catalogue every one is contentStatus
     "approved" — so every one is served to ordinary members — and five are
     reviewStatus "reviewed". Fifty-nine carry sets, reps, rest, setup steps
     and a progression rule that no qualified person has signed off.

     The app already said so on `machine`, on `swap`, on `player` and in Gym
     Mode. It did NOT say so on `workout`, which is the screen a member is on
     for the whole session — the one showing the prescription as a badge, the
     rest interval and the set log. The claim was never false; it was absent
     from the one place it carries weight, and saying it on the setup screen
     while withholding it there teaches a member to read silence as a
     sign-off.

     Shared rather than retyped, because two wordings of the same state is how
     one of them drifts into a stronger claim than the other. */
  function reviewLine(stations) {
    var list = [].concat(stations || []).filter(Boolean);
    if (!list.length) return '';
    var unreviewed = list.filter(function (st) {
      return st.reviewStatus !== 'reviewed' || !st.reviewer;
    });
    /* ANY unreviewed work means the session is not reviewed. Naming the
       reviewer of one station at the top of a mixed session would read as a
       sign-off on everything underneath it. */
    if (unreviewed.length) {
      /* NO "YET". THE QUEUE IS NOT MOVING.
         This read "Not yet reviewed by a coach" while review was expected to
         continue — one program had cleared and the rest were waiting, so "yet"
         described a real state. From 2026-10-22 it does not: the reviewer is
         not engaging and none is engaged in his place, so the word promised 59
         stations' worth of something nobody is doing.

         The facts are unchanged and are not softened. A member is still told
         no qualified person checked this, and still told to stop if it hurts.
         What goes is the implied promise, replaced by who DID write it, which
         is the honest substitute. Same reason the workout screen stopped
         saying "sessions appear here once approved": blaming a review process
         for a gap in what has been written is a lie a tester caught once. */
      return note('Written by the BullFit team, not checked by a qualified coach. Stop if anything hurts, and ask gym staff if you are unsure.', true);
    }
    var names = [];
    list.forEach(function (st) {
      if (names.indexOf(st.reviewer) === -1) names.push(st.reviewer);
    });
    var on = list[0].reviewedOn;
    return '<p class="bf-sub" style="font-size:12px">Reviewed by ' +
           esc(names.join(', ')) + (on ? ' · ' + esc(on) : '') + '</p>';
  }
  A.__reviewLine = reviewLine;

  /* The time equivalent of incompatibleNote(). Equipment has always had a way
     back — "This session is written for X" plus a button to change it. Time
     was the one dimension that stranded people in silence. */
  function lengthWayOut(plan) {
    var lens = (plan && plan.supportedSessionLengths) || [];
    if (!lens.length) return '';
    var list = lens.length === 1 ? lens[0]
             : lens.slice(0, -1).join(', ') + ' or ' + lens[lens.length - 1];
    return note('Nothing is written for the session length you gave. There ' +
                (lens.length === 1 ? 'is a program' : 'are programs') +
                ' written for ' + esc(list) + ', with everything else you told us unchanged.', true) +
      '<div class="bf-actions">' +
        btn('Change my answers', null, 'cyan',
            'data-href="' + esc(CFG.landingUrl || '/pages/bullfit-training') + '#bf-onboarding"') +
      '</div>';
  }
  A.__lengthWayOut = lengthWayOut;

  A.__incompatibleNote = incompatibleNote;
  function incompatibleNote(pr) {
    if (!pr || !pr.noCompatiblePlan) return '';
    var alt = pr.supportedEquipment || [];
    return note('No approved station in this session works with ' +
      (pr.chosenEquipment || 'your equipment') + '. ' +
      (alt.length
        ? 'This session is written for ' + alt.join(' or ') + '.'
        : 'Nothing else in this session is approved yet either.'), true) +
      (alt.length ? '<div class="bf-actions">' +
        btn('Change my equipment', null, 'ghost small',
            'data-href="' + esc(CFG.landingUrl || '/pages/bullfit-training') + '#bf-onboarding"') +
        '</div>' : '');
  }

  /* WHERE "START" GOES DEPENDS ON WHO IS ASKING.
   *
   * Every level used to land on the first-visit checklist, so an experienced
   * lifter had to walk through machine identification before being allowed to
   * train. Guidance level decides the default destination and the wording; it
   * never changes the program itself, and help stays one tap away at every
   * level — an experienced member still meets an unfamiliar machine.
   *
   * Unknown guidance means we have not asked yet, and the guided path is the
   * safe default for someone we know nothing about. */
  function guidanceLevel() {
    var pref = S.preferences && (S.preferences.preference || S.preferences);
    if (pref && pref.guidance) return pref.guidance;
    /* A MEMBER WHO ANSWERED THE OLD QUESTION KEEPS THAT ANSWER. Accounts
       created before the comfort question record `experience` and no
       guidance. Reading only `guidance` would quietly demote an experienced
       member to the beginner path — a stored answer must not be lost just
       because the question that produced it was replaced. */
    if (pref && pref.experience && A.__guidanceFor) return A.__guidanceFor(pref.experience);
    var local = A.__onboardingState ? A.__onboardingState() : {};
    /* `comfort` is the questionnaire's own key; `guidance` is what it is
       called once saved. Both are read, because a visitor reaches this screen
       between answering and signing in. */
    if (local.comfort) return local.comfort;
    if (local.guidance) return local.guidance;
    if (local.experience && A.__guidanceFor) return A.__guidanceFor(local.experience);
    return 'guided';
  }

  /* THE ONE BUTTON THE MEMBER IS MEANT TO PRESS.
     Where it goes is decided by how much guidance they want, and by whether
     they are already part-way through today's session.

     Guidance changes the DESTINATION only for a session not yet started: a
     beginner is walked into the gym first. Once they have logged a set they
     are already standing at the machine, so every level goes back to the
     workout — sending a beginner to the FIRST VISIT checklist at that point
     would lose their place, and they are the member least able to afford it.
     The gym guide stays in the navigation for whenever they want it. */
  /* WHERE THE MEMBER IS IN THE WORKOUT THEY ARE CURRENTLY IN.
     Scoped by OCCURRENCE — `<template>:<local day>` — which the backend
     resolves and returns as `activeOccurrence`, because only it knows the
     member's timezone and whether an unfinished session has carried over
     midnight. This used to compare `logged_at` to the browser's idea of today,
     which split a workout in half at 00:00 and told a member part-way through
     that they had not started.

     Returns the whole picture rather than just an index: the screen needs the
     per-set detail anyway, and deriving it twice invites the copies to
     disagree. */
  function sessionProgress(stations, loggedSets, occurrenceId) {
    var byStation = {};
    if (occurrenceId) {
      (loggedSets || []).forEach(function (l) {
        if (String(l.planned_session_id) !== String(occurrenceId)) return;
        var k = String(l.station_id);
        (byStation[k] || (byStation[k] = {}))[Number(l.set_index)] = l;
      });
    }

    var perStation = (stations || []).map(function (st) {
      var target = st.sets || 3;
      var done = byStation[String(st.id)] || {};
      var count = 0, next = 0;
      for (var i = 1; i <= target; i++) {
        if (done[i]) count++;
        else if (!next) next = i;
      }
      return { station: st, target: target, logged: done, doneCount: count,
               nextSet: next || target, complete: count >= target };
    });

    /* THE FIRST UNFINISHED SET. This is what the LOG says, and it is not the
       same thing as where the member has navigated to — see savedPosition().
       One is derived, the other is their choice, and conflating them is what
       made "See alternatives" throw away their place. */
    var firstUnfinished = -1;
    for (var j = 0; j < perStation.length; j++) {
      if (!perStation[j].complete) { firstUnfinished = j; break; }
    }
    return {
      perStation: perStation,
      occurrenceId: occurrenceId || null,
      sessionComplete: perStation.length > 0 && firstUnfinished === -1,
      firstUnfinishedIndex: firstUnfinished === -1
        ? Math.max(perStation.length - 1, 0) : firstUnfinished,
      started: perStation.some(function (p) { return p.doneCount > 0; })
    };
  }

  /* THE EXERCISE THE MEMBER CHOSE TO BE ON, kept for the duration of the
     workout rather than the duration of the screen.
     Stepping to another station, or leaving to read the setup guide or pick an
     approved alternative, must come back to the same exercise — those are part
     of doing the workout, not leaving it. Keyed by occurrence, so tomorrow's
     session starts from the log instead of reopening yesterday's place, and
     held in sessionStorage so a reload keeps it too. */
  var POS_KEY = 'bf_workout_pos';
  function savedPosition(occurrenceId) {
    if (!occurrenceId) return null;
    try {
      var raw = sessionStorage.getItem(POS_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || v.occurrence !== occurrenceId) return null;
      return typeof v.index === 'number' ? v.index : null;
    } catch (e) { return null; }
  }
  function savePosition(occurrenceId, index) {
    if (!occurrenceId) return;
    try {
      sessionStorage.setItem(POS_KEY,
        JSON.stringify({ occurrence: occurrenceId, index: index }));
    } catch (e) { /* private mode: the derived position still works */ }
  }
  A.__sessionProgress = sessionProgress;
  A.__savedPosition = savedPosition;
  A.__savePosition = savePosition;

  /* `equipment` is optional and the behaviour without it is unchanged, so the
     two callers that cannot know it keep today's route. */
  function primaryAction(inProgress, equipment) {
    var g = guidanceLevel();
    if (inProgress) {
      return { label: g === 'direct' ? 'Resume workout →' : 'Back to my workout →',
               route: 'workout' };
    }
    if (g === 'direct') return { label: 'Start workout →', route: 'workout' };
    if (g === 'overview') return { label: 'Start today\u2019s workout →', route: 'workout' };
    /* GYM MODE IS FOR FINDING MACHINES. A guided member on the approved
       bodyweight program was sent there anyway and met "Find your machine",
       "Confirm the match" and "Box squat to a chair — first machine to find".
       There is no machine. Guided still means guided: the workout screen walks
       them through setup and an easier version for each exercise. */
    /* ...and "not bodyweight" is not the same as "in a gym". A member who
       answered HOME DUMBBELLS was sent to Gym Mode and told to find and
       identify her machine. She has a pair of dumbbells in her house. The test
       has to be whether the equipment is a gym's, not whether it is anything
       other than bodyweight — found by a tester on her own phone, who pressed
       "Guide me through my workout" and landed on "Find your machine". */
    /* UNKNOWN still means Gym Mode. An unanswered member may well be standing
       in a gym, and the first-visit path prepares them; that contract is older
       than this fix and is asserted in next-action.test.js. The change is only
       for equipment we KNOW is not a gym's. */
    var eq = equipment || '';
    if (eq && !/gym|machine/i.test(eq)) {
      return { label: 'Guide me through my workout →', route: 'workout' };
    }
    return { label: 'Guide me through my workout →', route: 'firstgym' };
  }
  A.__guidanceLevel = guidanceLevel;
  A.__primaryAction = primaryAction;

  /* WHY THIS PLAN — rendered from the backend's own comparison.
     Every line is one criterion the program declared and what the member
     actually answered, so the explanation IS the decision rather than a
     sentence written to sound like one. Shared by the plan preview, the week
     and the home panel so the three cannot tell different stories. */
  var CRITERION = {
    goal: 'Your goals', experience: 'Your experience', routine: 'Right now',
    equipment: 'Your equipment', schedule: 'Your week', duration: 'Your time'
  };
  var VERDICT_TAG = {
    fits: ['Fits', 'cyan'], unknown: ['Not asked', ''], conflicts: ['Does not fit', '']
  };

  function matchRows(sel) {
    if (!sel || !sel.matches) return '';
    return sel.matches.map(function (m) {
      var t = VERDICT_TAG[m.verdict];
      if (!t) return '';
      return row(CRITERION[m.criterion] || m.criterion, m.note, tag(t[0], t[1]));
    }).join('');
  }

  /* Whether we may describe this as matched at all. `justified` is the
     backend's answer and does NOT depend on how many programs exist — with
     one program and a member who answered nothing it is false, and the copy
     here has to say so instead of implying a fit. */
  /* ONLY CRITERIA THE QUESTIONNAIRE ACTUALLY ASKS.
     `experience` is not one of them — guidance comes from the comfort
     question and experience stays unknown until a member volunteers it. It
     was listed here, so the selector's permanent `experience: unknown` was
     presented as a question still open, with a repair button that said "tell
     us how experienced you are" and opened GOALS, because that is the route
     it was mapped to. A member who had answered all six was told one was
     missing and sent to a question they had already answered. */
  var ASKS = {
    goal: ['goal', 'what you want from training'],
    routine: ['goal', 'what your training looks like right now'],
    equipment: ['equipment', 'where you train'],
    schedule: ['schedule', 'which days you are free'],
    duration: ['schedule', 'how long you have']
  };

  /* PROVISIONAL UNTIL EVERY CRITERION IS ESTABLISHED.
     A plan chosen on four answers out of six is a reasonable suggestion, not a
     settled fit, and saying "matched to you" over it would claim more than was
     checked. So an incomplete profile gets the word PROVISIONAL, the criteria
     that DO support it, and a direct link back to each question still open. */
  function planBasis(sel) {
    if (!sel) return '';
    /* A criterion nobody is asked about cannot be a question still open. */
    var unknown = (sel.unknown || []).filter(function (c) { return !!ASKS[c]; });
    var fits = (sel.matches || []).filter(function (m) { return m.verdict === 'fits'; });

    if (!sel.justified) {
      return note('PROVISIONAL. None of your answers have been checked against this '
        + 'program yet, so it is a starting point rather than a plan chosen for you.')
        + missingList(unknown);
    }
    if (unknown.length === 0) return '';
    return note('PROVISIONAL. Chosen on ' + fits.length + ' of your answers — '
      + fits.map(function (m) { return (ASKS[m.criterion] || ['', m.criterion])[1]; }).join(', ')
      + '. ' + unknown.length + ' question' + (unknown.length === 1 ? '' : 's')
      + ' still open, so this is not settled yet.') + missingList(unknown);
  }

  /* Each open question links to the screen that asks it. Naming a gap without
     a way to close it just makes the member feel behind. */
  function missingList(unknown) {
    if (!unknown || !unknown.length) return '';
    return '<div class="bf-actions">' + unknown.map(function (c) {
      var a = ASKS[c];
      return a ? btn('Tell us ' + a[1], a[0], 'ghost small') : '';
    }).join('') + '</div>';
  }
  A.__planBasis = planBasis;

  /* THE SCHEDULE IS PROPOSED, NEVER IMPOSED.
     The days a member ticked are when they are FREE. A program asking for
     three sessions uses three of them and leaves the rest alone — but that is
     a suggestion they accept, not a quiet rewrite of their answer. */
  function schedulePanel(sel) {
    var p = sel && sel.proposedSchedule;
    if (!p) return '';
    var rot = (p.rotation || []).length
      ? p.rotation.map(function (x) {
          return row(x.day, sessionLabel(x.sessionId));
        }).join('')
      : row('Sessions', p.sessionsPerWeek + ' a week on ' + p.days.join(', '));
    return panel('Your week', rot +
      '<p class="bf-sub">' + esc(p.note) + '</p>' +
      (p.fewerThanAvailable
        ? '<div class="bf-actions">' +
            '<button class="bf-btn small cyan" data-accept-schedule="' + esc(p.days.join(',')) + '">' +
              'Use these days</button>' +
            btn('Change my days', 'schedule', 'ghost small') +
          '</div>' +
          '<p class="bf-note" data-schedule-msg role="status" aria-live="polite"></p>'
        : ''));
  }

  function sessionLabel(id) {
    var names = S.sessionNames || {};
    return names[id] || String(id || '').replace(/-/g, ' ');
  }

  A.__matchRows = matchRows;
  A.__schedulePanel = schedulePanel;

  function accessLine(access) {
    if (!access) return '';
    if (access.reason === 'comped') return tag('Preview access', 'yellow');
    if (access.hasAccess) return tag(access.reason === 'cancelled_within_paid_period'
      ? 'Active until period ends' : 'Active member', 'cyan');
    return tag(access.reason === 'never_paid' ? 'Not a member yet' : access.reason, '');
  }

  /* ================= TRAINING ========================================== */

  /* THE FIRST WORKOUT HOME.
     Three answers, in this order, per the design: what to do today, why this
     plan fits this member, and how progress begins. One workout, one primary
     action, and an honest baseline — a new member is told their first log
     establishes it rather than shown invented achievements.

     Everything comes from /profile: the selected programme, the criterion
     comparison, and the member's own records. Nothing here is a second source
     of profile state. */
  A.register('home', async function () {
    var me = await load('me', '/me');
    var profile = await load('profile', '/profile', true);
    var rep = (profile && profile.reported) || {};
    var eng = (profile && profile.engagement) || {};
    var plan = (profile && profile.plan) || {};
    var active = eng.activeWorkout;

    /* AN UNFINISHED WORKOUT COMES FIRST. Someone mid-session does not want to
       be offered a new one; resume precedence is the whole point of having
       the occurrence model underneath. */
    var inProgress = !!(active && active.started && !active.complete);
    var pa = primaryAction(inProgress, rep.equipment);
    var guidance = guidanceLevel();

    /* ---- 1. WHAT TO DO TODAY ---- */
    var stations = active ? active.stations : [];
    var list = stations.length
      ? stations.map(function (x, n) {
          var done = x.loggedSets >= x.targetSets;
          return row(('0' + (n + 1)).slice(-2) + ' \u00b7 ' + x.exerciseName,
            x.loggedSets + ' of ' + x.targetSets + ' sets',
            done ? tag('Done', 'cyan') : (inProgress && n === active.firstUnfinishedIndex
              ? tag('Next', 'yellow') : ''));
        }).join('')
      : (plan.sessionId
          /* THREE REASONS THIS LIST CAN BE EMPTY, AND THEY ARE DIFFERENT FACTS.
             One message used to cover all three and it named the wrong cause
             for two of them.
               - no entitlement: about the account, not the program;
               - a started session that returned no stations: genuinely a
                 content problem, which is what the approval wording is for;
               - nothing started yet: the ordinary case, and the one an
                 entitled member on an approved program actually hits. */
          ? ((me && me.access && !me.access.hasAccess)
              ? note('Workout access is not enabled for this account yet. Your program is ' +
                     'chosen and your answers are saved \u2014 memberships are not open yet.', true)
              : active
                /* NOT "once the content is approved". That is the sentence a
                   tester read while her content WAS approved, recorded in the
                   comment above the workout screen's empty branch — and since
                   every station was approved on 2026-10-11 it can no longer be
                   true for anyone. A started session that returns no stations
                   is now unexpected, and the honest version does not guess at
                   a cause or hand the member a job they cannot do. */
                ? empty('No exercises came back for this session. Nothing you did ' +
                        'caused that \u2014 if it keeps happening, tell us.')
                : empty('Your exercises appear when you start today\u2019s session.'))
          : incompatibleNote({ noCompatiblePlan: plan.noCompatiblePlan,
                               supportedEquipment: plan.supportedEquipment }) ||
            /* "Released" implied an approval queue, which is the same wrong
               cause the workout screen gave. Nothing is waiting to be
               released; nothing has been WRITTEN for this combination. */
            /* NAME THE ANSWER THAT HAS NOTHING BEHIND IT.
               This said only "Nothing is written yet for this combination of
               answers" — true, and a dead end. One in five answer sets a real
               customer can give lands here, and EVERY one of them is a single
               session length away from a program that exists: the selector now
               reports which lengths those are.

               What it deliberately does not say is that the suggested length
               suits them. Whether a shorter session is right for someone
               already training regularly is the judgement the catalogue
               reserves for a qualified reviewer. This says what is written and
               what is not, and leaves the choosing to the member. */
            lengthWayOut(plan) ||
            empty('Nothing is written yet for this combination of answers.'));

    var today = panel('',
      '<div class="bf-eyebrow">What to do today</div>' +
      '<h2 class="bf-today-name">' + esc(plan.sessionName || 'Your starting block') + '</h2>' +
      '<p class="bf-sub">' + esc([rep.equipment, rep.sessionLength ? rep.sessionLength + ' available' : '']
        .filter(Boolean).join(' \u00b7 ')) + '</p>' + list +
      '<div class="bf-today-guid"><b>' + esc(guidanceLabelFor(guidance)) + '</b><p>' +
        /* The guided line named a machine for every member, including the one
           whose approved program is bodyweight and has no machine to find.
           Keyed off the equipment the PROGRAM uses, not off the guidance level
           alone, so machine members keep the machine wording. */
        esc(guidance !== 'guided'
          ? 'Go straight to your session. Tap exercise help whenever you want a refresher.'
          : /bodyweight/i.test(rep.equipment || '')
            ? 'We\u2019ll walk you through each exercise, its setup and an easier version before your first set.'
            : 'We\u2019ll help you find the first machine and see its setup before your first set.') +
      '</p></div>' +
      '<div class="bf-actions">' + btn(pa.label, pa.route, 'primary') +
        btn('See my full plan', 'week', 'ghost') + '</div>');

    /* ---- 2. WHY THIS FITS YOU ---- */
    var fits = (plan.matches || []).filter(function (m) { return m.verdict === 'fits'; });
    var why = fits.length
      ? panel('Why this fits you', fits.slice(0, 3).map(function (m) {
          return row(WHY_TITLE[m.criterion] || m.criterion, m.note, tag('Fits', 'cyan'));
        }).join('') + ((plan.unknown || []).length
          ? note((plan.unknown || []).length + ' question' +
              ((plan.unknown || []).length === 1 ? '' : 's') +
              ' still unanswered, so this is a starting point rather than a settled fit.')
          : ''), 'fits you')
      : panel('Why this fits you',
          empty('Answer the plan questions and the reasons appear here.') +
          '<div class="bf-actions">' + btn('Answer them', 'routine', 'ghost small') + '</div>',
          'fits you');

    /* ---- 3. HOW PROGRESS BEGINS ----
       Before any record exists, say so. Never fill these with invented
       achievements, and never imply a streak nobody has earned. */
    var logged = eng.completionsAllTime || 0;
    var setsLogged = active ? active.stations.reduce(function (a, x) { return a + x.loggedSets; }, 0) : 0;
    var baseline = panel('',
      '<div class="bf-eyebrow">How you will see progress</div>' +
      '<h2 class="bf-today-name">' + (logged
        ? 'Your <span class="bf-accent">progress</span> so far.'
        : 'Build your <span class="bf-accent">baseline</span>.') + '</h2>' +
      '<p class="bf-sub">' + esc(logged
        ? 'Counted from the workouts you reported \u2014 nothing else.'
        : 'Your first workout gives you something real to build on.') + '</p>' +
      row('Completed workouts', logged ? String(logged) : 'None logged yet') +
      /* TWO ROWS, TWO CLOCKS, AND IT HAS TO SAY WHICH.
         `logged` above is completionsAllTime; `setsLogged` is the ACTIVE
         occurrence only. Sitting adjacent and unlabelled, a member with two
         completed workouts and nothing logged yet TODAY read:

             Completed workouts   2
             Weights and reps     Ready for your first log

         while the workout screen one tap away showed "Last time: 12 reps" on
         every exercise. Caught by walking the live app. The true branch
         already said "this session"; the false branch implied never, which is
         the app telling someone their logging did not happen — the same thing
         a tester reported on 2026-10-09, done a different way. */
      row('Weights and reps', setsLogged
            ? setsLogged + ' sets logged this session'
            : (logged ? 'Nothing logged in this session yet'
                      : 'Ready for your first log')) +
      row('Banked weeks', String(eng.bankedWeeks || 0)) +
      note(logged ? 'Open your progress for the full record.'
                  : 'Log your first session to start seeing your progress here.') +
      '<div class="bf-actions">' + btn('My progress', 'progress', 'ghost small') + '</div>');

    return head('TODAY \u00b7 YOUR NEXT SESSION',
      'Hey' + (me && me.firstName ? ' ' + me.firstName : '') + '. ' +
        (inProgress ? 'Pick up where you left off.' : 'You\u2019ve got this.'),
      inProgress ? 'You have a workout in progress. Everything you logged is still there.'
                 : 'One useful session. One more step forward.',
      accessLine(profile && profile.access), inProgress ? 'Pick up' : 'You\u2019ve got this') +
      '<div class="bf-split"><div class="bf-stack">' + today + '</div>' +
      '<div class="bf-stack">' + why + baseline + '</div></div>';
  });

  var WHY_TITLE = {
    experience: 'Built for where you are', routine: 'Fits your routine right now',
    goal: 'Serves what you want to work toward', equipment: 'Your equipment',
    schedule: 'Your week', duration: 'Your available time'
  };
  function guidanceLabelFor(g) {
    return g === 'guided' ? 'Guided, step by step'
         : g === 'direct' ? 'Straight to the workout'
         : 'A quick overview, help nearby';
  }

  A.register('week', async function () {
    var prog = await load('progress', '/progress', true);
    var pr = await load('program', '/program', true);
    var stations = (pr && pr.stations) || [];
    var status = S.programStatus;

    var list = stations.length
      ? stations.map(function (st) {
          return row(st.exerciseName,
            /* THE REVIEWED PRESCRIPTION, through the one renderer that knows
               what it is. `repsRange` is Todd's instruction — 8 to 15 — and
               `reps` is the single number the logging box starts at. This read
               the seed, so My Plan said "8 reps" for four of the five
               exercises while the workout screen, calling repsLabel from this
               same file, showed the range. Two screens, one exercise, two
               different prescriptions. */
            (st.sets != null
              ? st.sets + ' sets · ' + repsLabel(st) + (/per side|second|plank/i.test(repsLabel(st)) ? '' : ' reps')
                + ' · ' + st.rest + ' rest'
              : ''),
            st.draftPreview ? tag('Draft', 'yellow') : '');
        }).join('')
      : status === 403
        ? note('The written plan is part of the paid program.', true)
        : incompatibleNote(pr) ||
          empty('No approved sessions yet. Your guides are reviewing them.');

    var week = prog && prog.week;
    var glance = week
      ? row('This week', week.completedSessions + ' of ' + week.requiredSessions + ' done')
        + row('Reflection', week.hasReflection ? 'Recorded' : 'Not yet')
        + row('Banked weeks', String(prog.bankedWeeks))
      : empty('Your week begins with your first session.');

    if (pr && pr.sessionNames) S.sessionNames = pr.sessionNames;
    var sel = pr && pr.selection;
    return head('YOUR TRAINING BLOCK', 'Your week, made clear.',
      'Training days are planned. Rest days count as rest.',
      btn('Text check-ins', 'sms', 'ghost small'), 'made clear') +
      '<div class="bf-split"><div class="bf-stack">' +
        panel((pr && pr.sessionName) || CFG.sessionName || 'Your session', list) +
        (sel ? panel('Why this plan', matchRows(sel)) + planBasis(sel) : '') +
      '</div>' +
      '<div class="bf-stack">' +
        schedulePanel(sel) +
        panel('At a glance', glance) +
        /* THE WAY BACK TO THE QUESTIONS, FROM INSIDE THE APP.
           Nothing in the member app linked to the questionnaire — not Today,
           not here, not progress. The only routes back were the marketing
           landing page and a URL you had to already know. Worse, the one
           visible "Review my answers" button on the save screen renders only
           when the member has NO plan, so it disappears at exactly the moment
           someone wants to change the answers that produced the plan they are
           looking at.

           Two testers reported this independently as not being allowed to go
           back and change their answers. It was always possible. Nothing said
           so, and a route nobody can find is not a route. */
        panel('Your preferences', row('Equipment, days, time and the rest',
          'Changing them re-picks your plan. Nothing you have logged is lost.',
          btn('Change preferences', 'routine', 'ghost small'))) +
        panel('', '<div class="bf-actions">' + (function () { var pa = primaryAction(false);
          return btn(pa.label, pa.route, 'primary'); })() + '</div>') +
      '</div></div>';
  });

  /* Does this program send the member to equipment they have to find? Read
     from the STATIONS, so it follows the program rather than the answer the
     member gave — a bodyweight program is a bodyweight program however they
     described their gym. */
  function needsGym(stations) {
    return (stations || []).some(function (st) {
      return /gym|machine/i.test(st.equipment || '');
    });
  }

  A.register('firstgym', async function () {
    var pr = await load('program', '/program', true);
    var stations = (pr && pr.stations) || [];

    /* THE BODYWEIGHT PROGRAM HAS NO MACHINES IN IT.
       This screen was written for walking into a gym, and the nav item leads
       here whatever the member is on. A member on the approved bodyweight
       program was shown "Find your machine", "Identify a machine" and "Box
       squat to a chair — first machine to find". The checklist is right for a
       first gym visit and wrong for a chair in a living room, so the program
       decides which one is shown. */
    if (!needsGym(stations)) {
      var order = stations.length
        ? stations.map(function (st, i) {
            return row(st.exerciseName, i === 0 ? 'Start here' : 'Then this one',
              i === 0 ? tag('Next', 'cyan') : '');
          }).join('')
        : incompatibleNote(pr) ||
          empty('Today\u2019s session appears once it is approved.');

      var plainSteps =
        row('01 \u00b7 Make a bit of space', 'You need room to stand, and a sturdy chair or step.',
          '') +
        row('02 \u00b7 Read the setup for each exercise',
          'Every movement has its own instructions, written out.',
          btn('Exercise guides', 'videos', 'small cyan')) +
        row('03 \u00b7 Take the easier version if you need it',
          'Each exercise names one, and there is a standing option where the floor is a problem.',
          '') +
        row('04 \u00b7 Follow one step at a time',
          'Your exercise, sets and next action stay on screen.',
          btn('Open workout', 'workout', 'small ghost'));

      return head('YOUR FIRST SESSION', 'Know exactly where to start.',
        'What to expect before your first set \u2014 no equipment needed.',
        tag('Beginner friendly', 'yellow'), 'where to start') +
        '<div class="bf-split"><div class="bf-stack">' +
          panel('Before your first set', plainSteps) +
          note('Stop an exercise that causes pain. If a movement does not feel right, take the easier version named with it.') +
        '</div><div class="bf-stack">' +
          panel('Today\u2019s session', '<div class="bf-eyebrow">' +
            esc((pr && pr.sessionName) || CFG.sessionName || 'Your session') + '</div>' + order +
            '<div class="bf-actions">' + btn('Open today\u2019s workout \u2192', 'workout', 'primary') + '</div>') +
        '</div></div>';
    }

    var route = stations.length
      ? stations.map(function (st, i) {
          return row(st.exerciseName, i === 0 ? 'First machine to find' : 'Then this one',
            i === 0 ? tag('Next', 'cyan') : '');
        }).join('')
      : incompatibleNote(pr) ||
        empty('Today\u2019s route appears once sessions are approved.');

    var steps =
      row('01 \u00b7 Find your machine', 'Search by name or look up the label on the equipment.',
        btn('Find equipment', 'machines', 'small cyan')) +
      row('02 \u00b7 Confirm the match', 'Check model and adjustment points. Ask gym staff when uncertain.',
        btn('Identify a machine', 'identify', 'small ghost')) +
      row('03 \u00b7 ' + (stations.some(function (st) { return !!st.video; })
            ? 'Watch the setup' : 'Read the setup'),
        'See the approved setup and movement for the matched equipment.',
        btn('View setup', 'machine', 'small ghost')) +
      row('04 \u00b7 Follow one step at a time', 'Your exercise, sets and next action stay on screen.',
        btn('Open workout', 'workout', 'small ghost'));

    return head('GYM MODE \u00b7 FIRST VISIT', 'Know exactly where to start.',
      'Your personal checklist for walking into the gym with a plan.',
      tag('Beginner friendly', 'yellow'), 'where to start') +
      '<div class="bf-split"><div class="bf-stack">' +
        panel('Before your first set', steps) +
        note('A photo cannot confirm safe setup. If your machine differs from the guide, ask gym staff or choose a reviewed alternative. Stop an exercise that causes pain.') +
      '</div><div class="bf-stack">' +
        panel('Today\u2019s route', '<div class="bf-eyebrow">' + esc(CFG.sessionName || 'Foundation A') + '</div>' + route +
          '<div class="bf-actions">' + btn('Find my first machine \u2192', 'machines', 'primary') + '</div>') +
      '</div></div>';
  });

  A.register('machines', async function () {
    var pr = await load('program', '/program', true);
    /* ONLY THE EXERCISES THAT INVOLVE A MACHINE.
       This listed every station in the member's program and called each one a
       machine. Seen on a phone against the live site, a bodyweight member was
       shown "Box squat to a chair — confirm the model in your gym before you
       start." There is no model of box squat and there is no gym. Every
       station already carries `equipment`, which is the field that answers
       this; nothing had ever consulted it.

       It is not cosmetic. The screen exists to stop someone using the wrong
       machine, and it was issuing an instruction that cannot be carried out. */
    var all = (pr && pr.stations) || [];
    var stations = all.filter(function (st) {
      return st.equipment && String(st.equipment).toLowerCase() !== 'bodyweight';
    });
    var cards = stations.length
      ? '<div class="bf-grid three">' + stations.map(function (st) {
          return '<div class="bf-panel"><h3 style="margin:0 0 6px">' + esc(st.exerciseName) + '</h3>' +
            '<p class="bf-sub" style="font-size:13px">Confirm the model in your gym before you start.</p>' +
            '<div class="bf-actions">' + btn('See setup →', 'machine', 'ghost small') + '</div></div>';
        }).join('') + '</div>'
      /* Two different empty states, because they are two different facts. A
         program with movements but no equipment is not waiting on anything —
         saying it "fills in as sessions are approved" would send a bodyweight
         member away expecting machines that are never coming. */
      : (all.length
          ? empty('Your program does not use any machines \u2014 every movement in it is '
              + 'bodyweight, so there is nothing here to identify.')
          : incompatibleNote(pr) ||
            empty('The equipment library fills in once you have a program.'));
    return head('EQUIPMENT LIBRARY', 'Meet your machines.',
      'Choose an equipment family, then confirm the model in your gym.',
      btn('Help identify a machine', 'identify', 'ghost small'), 'your machines') + cards;
  });

  A.register('identify', async function () {
    return head('EQUIPMENT MATCH', 'What is this machine?',
      'Use the printed name and model label. A photo can help suggest a match.', '', 'this machine') +
      '<div class="bf-split">' +
        panel('Find the label', '<div class="bf-form">' +
          '<label>Machine name or model<input id="bf-model" placeholder="Example: seated chest press"></label>' +
          '</div>' + note('Identification is a guide, not a safety check. If the match is uncertain, ask gym staff.') +
          '<div class="bf-actions">' + btn('See matching setup', 'machine', 'cyan') + '</div>') +
        panel('If you cannot find it', row('Ask a member of staff', 'They can point you at the right machine in seconds.') +
          row('Choose an alternative', 'Alternatives work the same muscles.', btn('See alternatives', 'swap', 'ghost small'))) +
      '</div>';
  });

  A.register('machine', async function () {
    var pr = await load('program', '/program', true);
    /* The first station THAT USES A MACHINE, not simply the first. Same defect
       as the library one screen back: a bodyweight member arriving here was
       shown "MACHINE SETUP \u2014 Box squat to a chair". */
    var allSt = (pr && pr.stations) || [];
    var st = allSt.filter(function (x) {
      return x.equipment && String(x.equipment).toLowerCase() !== 'bodyweight';
    })[0];
    var status = S.programStatus;
    if (!st) {
      /* WHY THERE IS NOTHING HERE DECIDES WHAT TO SAY.
         Three different absences used to collapse into one sentence about the
         catalogue being empty. For a bodyweight member it was simply false:
         every station in the catalogue is approved and carries setup steps,
         their session just uses no machines. Gym Mode is in the member nav and
         its step 03 opens this screen, so that falsehood was two taps away.
         Found by walking the live app. */
      var hasStations = allSt.length > 0;
      return head('MACHINE SETUP', hasStations ? 'This session uses no machines.'
                                               : 'Setup is not available yet.', '') +
        panel('', status === 403
          ? note('Written setup is part of the paid program.', true)
          : incompatibleNote(pr) ||
            (hasStations
              ? note('Your session needs no equipment, so there is nothing to set up. '
                   + 'The setup for each exercise is written on the workout screen itself.', true)
                + '<div class="bf-actions">' + btn('Open workout \u2192', 'workout', 'cyan') + '</div>'
              : empty('No approved station has setup instructions yet.')));
    }
    var steps = '<ol style="margin:0;padding-left:20px;line-height:1.7">' +
      st.setupSteps.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ol>';
    var review = reviewLine([st]);
    return head('MACHINE SETUP', st.exerciseName,
      'Set it up the same way every time. That is what makes progress readable.',
      st.draftPreview ? tag('Draft preview', 'yellow') : '') +
      '<div class="bf-split"><div class="bf-stack">' + panel('Set it up', steps + review) + '</div>' +
      '<div class="bf-stack">' + panel('Your prescription',
        row('Sets', String(st.sets)) + row('Reps', String(st.reps)) + row('Rest', String(st.rest))) +
        panel('', '<div class="bf-actions">' + btn('Open workout →', 'workout', 'primary') +
          btn('See alternatives', 'swap', 'ghost') + '</div>') + '</div></div>';
  });

  A.register('swap', async function () {
    var pr = await load('program', '/program', true);
    var stations = (pr && pr.stations) || [];
    /* THE EXERCISE THEY ASKED ABOUT, NOT THE FIRST ONE.
       This was `stations[0]`, unconditionally. A member on the third exercise
       who tapped "See alternatives" was shown alternatives for the first —
       silently, and plausibly enough that they would just do the wrong
       substitute. The workout screen now passes the station it is on. */
    var wanted = (A.helpers.routeParam && A.helpers.routeParam()) || null;
    var st = null;
    for (var i = 0; i < stations.length; i++) {
      if (String(stations[i].id) === String(wanted)) { st = stations[i]; break; }
    }
    if (!st) st = stations[0];

    var alts = (st && st.alternatives) || [];
    var easier = (st && st.easier) || [];
    var standing = st && st.standingOption;

    /* Three different things a member might need, and they are not
       interchangeable: an equal swap when the equipment is busy, a way back
       down when it is too hard, and a version that does not need the floor.
       Showing only the first meant a member who could not get down on the
       floor was told there was no alternative, when one had been written. */
    var body = '';
    if (alts.length) {
      /* DO NOT CALL IT REVIEWED WHEN IT IS NOT.
         These are names in a list. None of them is a station: there are no
         setup steps for them, no progression, no video and no reviewer — a
         member swapping to one gets the name and nothing else. Labelling them
         "Reviewed alternative" claimed a sign-off that does not exist, and
         claimed it hardest on the screen a member reaches when something has
         already gone wrong. The station's own status is what can honestly be
         reported. */
      var reviewed = st && st.reviewStatus === 'reviewed';
      body += panel('Instead of this exercise',
        alts.map(function (a) {
          return row(a, reviewed
            ? 'Named in the reviewed program. Setup guidance is not written for it.'
            : 'Named in the program. Not reviewed yet, and no setup guidance is written for it.');
        }).join(''));
    }
    if (easier.length) {
      body += panel('If it is too hard',
        easier.map(function (a) { return row(a, 'Easier version'); }).join(''));
    }
    if (standing) {
      body += panel('If you cannot get on the floor',
        row(standing.name, standing.steps));
    }
    if (!body) {
      body = panel(st ? st.exerciseName : 'Alternatives',
        empty('No approved alternative has been reviewed for this station yet. ' +
              'Skip it and carry on, or ask gym staff — we would rather say nothing ' +
              'than invent a substitute.'));
    }

    return head('ALTERNATIVES', st ? st.exerciseName : 'Alternatives',
      'These work the same movement. Anything not listed has not been reviewed.',
      '', st ? '' : 'does not suit you') +
      body +
      '<div class="bf-actions">' + btn('Back to my workout', 'workout', 'primary') +
      btn('Back to setup', 'machine', 'ghost') + '</div>';
  });

  /* ---- RENDERING WHAT A REVIEWER WROTE ---------------------------------
     A reviewed program is not a list of exercises. It carries the shape of
     the session, how hard to push, how to make each movement harder or
     easier, what to do if you cannot get on the floor, and when to stop.
     Rendering only the stations showed five straight sets where there are two
     circuits, a single rep number where there is a range, and dropped the
     stop criteria entirely. These build the missing parts. */

  /** "8" when the prescription says 8-15 is a different prescription. */
  function repsLabel(st) {
    if (st.repsText) return st.repsText;
    if (st.repsRange) return st.repsRange.min + '\u2013' + st.repsRange.max;
    return st.reps == null ? '' : String(st.reps);
  }

  A.__repsLabel = repsLabel;

  /* THE SESSION AS BLOCKS, IN ORDER.
     A circuit is a group of exercises performed together, and the screen used
     to show one exercise at a time and jump between them after every set. A
     tester described the result exactly: the buttons flipping between "box
     squat", "incline pushups" and "wall hip hinge" is confusing and
     unorganised. The training was right — alternate, rest after the pair — and
     the presentation hid it.

     Stations without a block are their own group of one, so a program with no
     circuits renders exactly as before. */
  function blockGroups(guidance, stations) {
    var out = [], byKey = {};
    (stations || []).forEach(function (x, i) {
      var key = x.block || ('solo:' + x.id);
      if (!byKey[key]) { byKey[key] = { key: key, title: null, items: [] }; out.push(byKey[key]); }
      byKey[key].items.push({ station: x, index: i });
    });
    var declared = (guidance && guidance.blocks) || [];
    out.forEach(function (g) {
      for (var i = 0; i < declared.length; i++) if (declared[i].id === g.key) g.title = declared[i].title;
      if (!g.title) g.title = g.items.length > 1 ? 'Circuit' : g.items[0].station.exerciseName;
    });
    return out;
  }
  A.__blockGroups = blockGroups;

  function blockOf(guidance, st) {
    var blocks = (guidance && guidance.blocks) || [];
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i].id === st.block) return blocks[i];
    }
    return null;
  }

  /** Who else is in this circuit, in order, so the member can be told to
      alternate rather than finish one exercise and move on. */
  function partnersIn(stations, st) {
    if (!st.block) return [];
    return stations.filter(function (x) { return x.block === st.block; });
  }

  /* IN A CIRCUIT, REST COMES AFTER THE PAIR.
     The logger starts a rest timer from the station's own `rest`. Inside a
     circuit that would rest the member after the squat, when the written
     program has them go straight to the push-up and rest after both. So the
     timer is suppressed on every station but the last of its block. */
  /* THE NEXT THING TO DO, IN THE ORDER TODD WROTE IT.
     The screen offered "next exercise", so a member logged all three squats,
     moved on, and did all three push-ups — while the panel above told them to
     alternate. The instruction was right and the controls contradicted it.

     A circuit is a flat sequence of (exercise, round) pairs: squat 1, push-up
     1, squat 2, push-up 2, and so on. The next step is the first pair in that
     sequence that has not been logged. Outside a circuit the sequence is just
     this exercise's own sets, which is the same rule with a group of one. */
  function nextCircuitStep(stations, pos) {
    for (var b = 0; b < stations.length; b++) {
      var st = stations[b];
      var group = partnersIn(stations, st);
      if (!group.length) group = [st];
      if (group[0].id !== st.id) continue;        // only walk each block once
      var rounds = 0;
      group.forEach(function (g) {
        var pg = pos.perStation[indexOfStation(stations, g.id)];
        if (pg && pg.target > rounds) rounds = pg.target;
      });
      for (var r = 1; r <= rounds; r++) {
        for (var k = 0; k < group.length; k++) {
          var gi = indexOfStation(stations, group[k].id);
          var pg = pos.perStation[gi];
          if (!pg) continue;
          if (r <= pg.target && !pg.logged[r]) return { index: gi, round: r, station: group[k] };
        }
      }
    }
    return null;
  }

  function indexOfStation(stations, id) {
    for (var i = 0; i < stations.length; i++) if (stations[i].id === id) return i;
    return -1;
  }

  /* WHAT THIS EXERCISE IS ACTUALLY MEASURED IN.
     Every set row offered Weight and Reps. None of these five movements takes
     a weight, and the core set is prescribed as "6-10 per side, OR a 20-45
     second plank" — two different units, neither of which is a rep count in a
     box marked Reps.

     The options come from the prescription rather than a list kept here: an
     exercise named "A or B" offers A and B, anything named like a hold is
     measured in seconds, and a station with a standing alternative offers that
     too. Nothing is invented; the names are Todd's. */
  /* ONE RULE FOR WHAT A MOVEMENT IS MEASURED IN.
     This lived twice, as /plank|hold|bridge/ for the named versions and
     /plank|hold|angel/ for the standing one, which is how two lists of the
     same idea drift. A wall sit is timed and matched neither of them. */
  /* WHAT THE BOX ASKS FOR, WHEN ONLY A NAME IS AVAILABLE.
     Alternatives and standing options are bare strings with no data of their
     own, so the unit has to come from the name. The first version was a loose
     substring match — plank|hold|bridge|angel|wall sit|dead hang|carry — and
     ten of the seventeen names it matched were wrong.

     `bridge` caught every glute bridge, which a tester reported: "glute bridge
     is not counted in seconds, that still needs to be active reps". `angel`
     caught the wall angel, whose own instruction says to slide the arms up and
     down 8-12 TIMES. Worst of all, `hold` caught the balance aids — "assisted
     squat holding a doorframe", "single-leg calf raise holding a rail",
     "lightly hold a wall or counter for balance" — every one a rep exercise
     where you hold something to stay upright, and every one writing a member's
     reps into the seconds column.

     So: named movements, not fragments. A movement is timed when sustaining
     the position IS the exercise. `hold` counts only as the final word, which
     is what separates "hollow hold" from "holding a doorframe". Mountain
     climbers are here because the reviewer prescribes them in seconds and they
     are coming with his next batch; leaving them out would repeat this bug. */
  function unitOfName(name) {
    var n = String(name || '');
    return (/\b(?:planks?|wall sits?|dead hangs?|mountain climbers?)\b/i.test(n)
         || /\bcarr(?:y|ies)\b/i.test(n)
         || /\bholds?\s*$/i.test(n)
         || /\bseconds?\b/i.test(n))
      ? 'seconds' : 'reps';
  }
  A.__unitOfName = unitOfName;

  function loggingOptions(st) {
    var names = String(st.exerciseName || '').split(/\s+or\s+/i)
      .map(function (x) { return x.trim(); }).filter(Boolean);
    if (!names.length) names = [st.exerciseName || 'This exercise'];
    var opts = names.map(function (n) {
      return { id: n, name: n, unit: unitOfName(n) };
    });
    if (st.standingOption && st.standingOption.name) {
      opts.push({ id: st.standingOption.name, name: st.standingOption.name,
                  unit: unitOfName(st.standingOption.name), standing: true });
    }

    /* THE ALTERNATIVES ARE LOGGABLE VERSIONS, NOT A READING LIST.
       "See alternatives" offered a member a different movement and then had
       nowhere to put the result: the set was still recorded against the
       machine they had walked away from. A tester reported it exactly that
       way — if you pick the alternate, you should be able to log it.

       It matters most for the member who has no machine. The alternatives are
       usually the free-weight or bodyweight version, so this is also the only
       route to logging a dumbbell movement on a machine program.

       The prescription on screen stays the station's own. An alternative is a
       swap for the movement, not a new set and rep scheme, and inventing one
       per alternative would be writing instruction nobody reviewed. */
    (st.alternatives || []).forEach(function (a) {
      var n = String(a || '').trim();
      if (!n) return;
      for (var i = 0; i < opts.length; i++) if (opts[i].id === n) return;
      opts.push({ id: n, name: n, unit: unitOfName(n), alternative: true });
    });

    return opts;
  }
  A.__loggingOptions = loggingOptions;
  /* Reps "per side" is still reps, but the member needs to know which number
     the box wants. Taken from the written prescription, not guessed. */
  function repsHint(st) {
    return /per side/i.test(st.repsText || '') ? 'Reps per side' : 'Reps';
  }
  /* THE CHOICE HAS TO SURVIVE THE SESSION.
     "Dead bug or forearm plank" is one station with two movements, and the
     counter plank is a third for a member who cannot get on the floor. Which
     one they picked changes the unit on the box and belongs in the record, so
     it is stored per occurrence and per station rather than held in the render. */
  function variantKey(occurrence, st) {
    return 'bf_variant:' + (occurrence || 'none') + ':' + st.id;
  }
  function selectedVariant(occurrence, st) {
    var opts = loggingOptions(st);
    var saved = null;
    try { saved = sessionStorage.getItem(variantKey(occurrence, st)); } catch (e) {}
    for (var i = 0; i < opts.length; i++) if (opts[i].id === saved) return opts[i];
    return opts[0];
  }
  function unitFor(st, occurrence) {
    return selectedVariant(occurrence, st).unit;
  }
  function variantPanel(occurrence, st) {
    var opts = loggingOptions(st);
    if (opts.length < 2) return '';
    var cur = selectedVariant(occurrence, st);
    return panel('Which version are you doing?',
      opts.map(function (o) {
        var on = o.id === cur.id;
        return '<button class="bf-btn small ' + (on ? 'cyan' : 'ghost') + '"' +
          ' data-pick-variant="' + esc(o.id) + '" data-station="' + esc(st.id) + '"' +
          ' data-occurrence="' + esc(occurrence || '') + '"' + (on ? ' aria-pressed="true"' : '') + '>' +
          esc(o.name) + (o.standing ? ' (standing)' : '') + '</button>';
      }).join(' ') +
      '<p class="bf-sub">Logged in ' + esc(cur.unit) + '. Your choice is saved with the set.</p>');
  }

  /* WHAT A SAVED SET SAYS IT WAS.
     This read `prior.reps ? ': ' + prior.reps + ' reps' : ''`, so a set
     measured in seconds — every plank, every hold — has `reps: null` and fell
     through to the bare word "Logged" with no value at all. Found on the live
     site: 30 seconds was saved, the server stored
     `{reps: null, seconds: 30, variant: "Counter plank"}` correctly, and the
     screen showed "Logged" next to an empty input and a Save button. A reps
     set one row above read "Logged: 10 reps". So the member sees the exercise
     they just held for 30 seconds reporting nothing, beside a control that
     still invites them to save it — the screen reads as if the work was lost
     when it was not. Seconds are the unit here, not the exception. */
  function loggedLabel(prior) {
    if (prior.reps) return 'Logged: ' + esc(prior.reps) + ' reps';
    if (prior.seconds) return 'Logged: ' + esc(prior.seconds) + ' seconds';
    return 'Logged';
  }

  A.__loggedLabel = loggedLabel;

  /* WHAT THEY DID LAST TIME ON THIS MOVEMENT.
   *
   * Three things decide whether this is honest rather than merely plausible,
   * and each one has a wrong answer that looks right.
   *
   * WHICH RECORD IS CURRENT. Re-logging a set is an upsert on one live row —
   * `corrects_id IS NULL` — so the row the backend returns already IS the
   * corrected value, and a member who fixed a typo last week sees the number
   * they meant. The schema also allows superseded rows carrying `corrects_id`;
   * /workouts filters those out, so nothing here has to choose between two
   * versions of the same set, and nothing here may assume it can.
   *
   * WHICH SESSION CAME FIRST. Not `logged_at`: the upsert sets it to now()
   * every time a set is re-saved, so correcting a number from two weeks ago
   * makes it the most recent row in the table. Ordering on it would show
   * yesterday's member the set they just fixed and call it last time. The
   * occurrence's own day is fixed at the moment the workout happened and is
   * never rewritten, so that is the clock. Row id breaks a tie within a day;
   * it is assigned once, at insert, and an edit does not move it either.
   *
   * WHAT COUNTS AS THE SAME EXERCISE. The movement recorded WITH the set, not
   * the station it sat at. Station ids are per session — a leg press is `f1`
   * in Foundations and `la1` in Split A — so matching on them would hide a
   * member's own history the day their program changed. Matching on the
   * station's prescribed name instead would be worse: a member who swapped to
   * the easier version last time would be shown those numbers as though they
   * were the harder movement. `variant` is what they actually did. A row saved
   * before that column existed has no movement recorded and is therefore not
   * matched — leaving it out can only ever understate the history, where
   * assuming it was today's exercise could put a wrong number in front of
   * someone about to load a machine. */
  function occurrenceDay(id) {
    var s = String(id == null ? '' : id);
    var cut = s.indexOf(':');
    return cut < 0 ? '' : s.slice(cut + 1);
  }

  function lastTimeFor(logged, currentOccurrence, movement) {
    if (!movement || !currentOccurrence) return null;
    var today = occurrenceDay(currentOccurrence);
    if (!today) return null;

    var byOccurrence = {};
    (logged || []).forEach(function (row) {
      if (!row || !row.variant) return;
      if (String(row.variant) !== String(movement)) return;
      var occ = String(row.planned_session_id || '');
      if (!occ || occ === String(currentOccurrence)) return;
      var day = occurrenceDay(occ);
      /* Strictly earlier. A second occurrence on the same day is this
         session's neighbour, not "last time". */
      if (!day || day >= today) return;
      var e = byOccurrence[occ] ||
        (byOccurrence[occ] = { occurrence: occ, day: day, maxId: 0, bySet: {} });
      var id = Number(row.id) || 0;
      if (id > e.maxId) e.maxId = id;
      e.bySet[Number(row.set_index)] = row;
    });

    var best = null;
    Object.keys(byOccurrence).forEach(function (k) {
      var e = byOccurrence[k];
      if (!best || e.day > best.day || (e.day === best.day && e.maxId > best.maxId)) best = e;
    });
    /* The most recent earlier session that CONTAINED this movement, which is
       not always the session immediately before: on an upper/lower split the
       last leg press is two sessions back, and that is the one being asked
       about. */
    return best;
  }

  function priorValue(row) {
    var done = row.reps ? row.reps + ' reps'
             : row.seconds ? row.seconds + ' seconds'
             : '';
    /* Weight is the number a member is deciding about when they ask, so it is
       not dropped the way loggedLabel drops it. Stored in minor units. */
    var w = Number(row.weight_milli);
    var load = row.weight_milli != null && !isNaN(w) && w > 0
      ? (Math.round(w / 10) / 100) + ' ' + (row.weight_unit || 'lb')
      : '';
    if (done && load) return done + ' at ' + load;
    return done || load || 'logged';
  }

  function lastTimeLabel(prev, setIndex) {
    if (!prev) return '';
    var row = prev.bySet[setIndex];
    /* A session that happened but has no row for THIS set is a different
       answer from no session at all, and silence would read as the latter. */
    if (!row) return 'No record for this set last time.';
    return 'Last time: ' + priorValue(row);
  }

  A.__lastTimeFor = lastTimeFor;
  A.__lastTimeLabel = lastTimeLabel;

  function takesWeight(st) {
    return !/bodyweight/i.test(st.equipment || '');
  }

  function restForLogging(stations, st) {
    var group = partnersIn(stations, st);
    if (group.length < 2) return st.rest;
    return group[group.length - 1].id === st.id ? st.rest : '';
  }

  function howHardPanel(guidance) {
    if (!guidance || !guidance.intensity) return '';
    return panel('How hard to push',
      '<p class="bf-sub">' + esc(guidance.intensity) + '</p>', 'yellow');
  }

  function warmupPanel(guidance) {
    var w = guidance && guidance.warmup;
    if (!w || !w.length) return '';
    return panel('Warm up first',
      '<ol class="bf-steps">' + w.map(function (x) {
        return '<li>' + esc(x) + '</li>';
      }).join('') + '</ol>');
  }

  /* STOP CRITERIA. Clinical content, and the one part of a program a member
     may need at the moment they are least able to go looking for it. It is
     rendered on the workout screen itself rather than behind a link, and the
     two instructions are kept apart: stopping an exercise and stopping to get
     help are not the same thing. */
  function safetyPanel(guidance) {
    var sf = guidance && guidance.safety;
    if (!sf) return '';
    return '<section class="bf-safety" aria-labelledby="bf-safety-h">' +
      '<h2 id="bf-safety-h">Pain and effort</h2>' +
      '<p class="bf-safety-normal">' + esc(sf.normal) + '</p>' +
      '<p class="bf-safety-stop"><strong>Stop that exercise</strong> ' +
        esc(String(sf.stopExercise).replace(/^Stop that exercise\s*/i, '')) + '</p>' +
      '<p class="bf-safety-help"><strong>Stop and get help</strong> ' +
        esc(String(sf.stopAndGetHelp).replace(/^Stop the workout and get help\s*/i, '')) + '</p>' +
      '</section>';
  }

  /** Harder, easier, the standing version, and the mistake worth naming. */
  /* `withSteps` asks for the approved setup steps as well, which is how a LONE
     exercise reaches them. On a circuit every exercise on the screen already
     carries its own steps on its own row (see setRowFor), so a copy here would
     be the current station's instruction shown twice; the caller passes false.

     Not on each row, for a lone exercise, because its rows are headed "Set 1",
     "Set 2", "Set 3" — the set number, not the movement. Hanging the same
     instruction off three set numbers would claim the steps belong to a set
     and repeat them three times. One disclosure, in the panel already named
     after the exercise, beside the rest of what Todd wrote about it. */
  function exerciseGuidance(st, withSteps) {
    var bits = '';
    if (st.tempo) bits += row('Tempo', esc(st.tempo), '');
    if (st.commonMistake) bits += row('Common mistake', esc(st.commonMistake), '');
    if (st.harderSteps && st.harderSteps.length) {
      bits += row('To make it harder',
        st.harderSteps.map(esc).join(' \u2192 '), '');
    }
    if (st.easier && st.easier.length) {
      bits += row('If it is too hard', st.easier.map(esc).join(' \u00b7 '), '');
    }
    if (st.standingOption) {
      /* Named rather than buried: a member who cannot get down on the floor
         otherwise loses the exercise with no explanation. */
      /* PLAIN TEXT, because row() escapes what it is given. This passed
         hand-built markup into that argument, so the member read a literal
         "<strong>Wall angel.</strong>" in the middle of Todd's reviewed
         instruction for the one person who cannot get on the floor. The same
         content renders correctly in bf-training-screens-b.js, which never
         wrapped it. */
      bits += row('If you cannot get on the floor',
        st.standingOption.name + '. ' + st.standingOption.steps, '');
    }
    /* INSIDE A .bf-row, because that is where this disclosure's styling lives:
       every .bf-howto rule in bf-training-app.css is scoped under .bf-row, so
       one placed outside a row loses the pointer, the caret, the suppression
       of Safari's own marker, and the step list's type. Same wrapper the rows
       use, so no new CSS is needed for it.

       EMPTY-WRAPPER GUARD, twice over: no steps, no disclosure, and no panel
       at all unless something goes in it. */
    var howTo = withSteps && st.setupSteps && st.setupSteps.length
      ? '<div class="bf-row"><div><details class="bf-howto"><summary>' +
          '<h3>Setup</h3><span class="bf-howto-cue">How do I do this?</span></summary>' +
          '<ol class="bf-howto-steps">' +
          st.setupSteps.map(function (s) { return '<li>' + esc(s) + '</li>'; }).join('') +
          '</ol></details></div></div>'
      : '';
    return (howTo || bits) ? panel('This exercise', howTo + bits) : '';
  }

  A.register('workout', async function () {
    var pr = await load('program', '/program', true);
    var stations = (pr && pr.stations) || [];
    var guidance = (pr && pr.guidance) || null;
    var sets = await api('/workouts?session=' + encodeURIComponent(CFG.sessionKey || ''));
    var logged = (sets.ok && sets.data.sets) || [];
    var occurrence = (sets.ok && sets.data.activeOccurrence) || null;
    if (!stations.length) {
      /* SAY WHICH OF THESE IT IS, BECAUSE THEY ARE NOT THE SAME PROBLEM.
         A tester reached this screen on her phone and read "Sessions appear
         here once approved." Her access was fine and the content WAS approved;
         what she had not done was save her answers, so there was nothing to
         build a session from. The screen blamed our review process for
         something she could have fixed in two taps, and gave her nowhere to go
         — she left and emailed instead, which is the good outcome only because
         she is a tester.
         `reported.equipment` is the cheapest honest test for "the questions
         were answered and saved": it is null until a save lands, and the
         questionnaire cannot be completed without it. */
      var saved = S.programStatus === 403 ? null : await load('profile', '/profile', true);
      var answersSaved = !!(saved && saved.reported && saved.reported.equipment);
      return head('GUIDED WORKOUT', 'No session to run yet.', '') +
        panel('', S.programStatus === 403
          ? note('The guided workout is part of the paid program.', true)
          : incompatibleNote(pr) ||
            (!answersSaved
              ? note('Your answers are not saved to your account yet, so there is ' +
                     'nothing to build today\u2019s session from. Finish the short ' +
                     'setup and your first session appears here.', true) +
                '<div class="bf-actions">' +
                  btn('Finish setting up', 'plan', 'primary') +
                '</div>'
              /* THE ANSWERS ARE SAVED AND THERE IS STILL NOTHING.
                 Then the program for THEIR equipment is written but not yet
                 approved for release, which is the true state of the dumbbell
                 and machine programs today — only the bodyweight one has been
                 through review. "Sessions appear here once approved" was
                 technically true and told the member nothing they could act
                 on: not which approval, not that another program is ready now,
                 and not how to reach it. A tester sat on this screen and had
                 to email to find out. Naming their own answer back to them is
                 also the only honest way to say it, because the sentence is
                 about their equipment, not about the catalogue. */
              /* THE REASON IS A COVERAGE GAP, NOT A REVIEW QUEUE.
                 This said the member's program "has not finished review yet",
                 and the comment above it recorded why that was true: on the
                 day it was written only bodyweight had been through review.
                 All 56 stations were approved on 2026-10-11, so from that
                 moment the sentence was false to every member who read it —
                 blaming our review process for what is actually a gap in what
                 has been WRITTEN. The screen has done this once before, and
                 the comment above describes a tester emailing because of it.

                 It also recommended bodyweight as ready now. True for a member
                 on thirty minutes and three days, and false for one on twenty
                 minutes or a single day — and nothing here knows which, because
                 that answer needs the option sweep. So this no longer names a
                 way out it cannot check. It states what is true for everyone
                 who lands here, and sends them to the questionnaire, which
                 computes the serveable options from the real selector and
                 already says honestly what it cannot cover. */
              : note('Nothing is written yet for this combination of answers \u2014 '
                     + 'your equipment, how long you have, and how many days. '
                     + 'Change any of them and the questions will show you what '
                     + 'we can cover.', true) +
                '<div class="bf-actions">' +
                  btn('Change my answers', 'equipment', 'primary') +
                '</div>'));
    }
    /* RESUME WHERE THEY LEFT OFF.
       This used to be `stations[0]`, unconditionally. A member who logged two
       exercises, put the phone away and came back was handed exercise one
       again — and since the screen shows a single exercise with no way
       forward, exercises two and three were unreachable in a session that had
       already started. The button said "Resume"; the screen restarted them. */
    var pos = sessionProgress(stations, logged, occurrence);
    var saved = savedPosition(occurrence);
    var idx = saved == null ? pos.firstUnfinishedIndex : saved;
    idx = Math.min(Math.max(idx, 0), stations.length - 1);
    savePosition(occurrence, idx);
    var here = pos.perStation[idx];
    var st = here.station;
    /* The warm-up belongs at the start of the session, not on top of every
       exercise for a member who is already three rounds in. */
    var anyLogged = pos.perStation.some(function (x) { return x.doneCount > 0; });

    /* THE WHOLE CIRCUIT ON ONE SCREEN, GROUPED BY ROUND.
       Everything the member does before moving on is now in front of them:
       Round 1 with its two exercises, then Round 2, then Round 3. Nothing
       flips mid-circuit, because there is nowhere else to go until it is
       done. A station outside a circuit is a group of one and renders as it
       always did. */
    var groups = blockGroups(guidance, stations);
    var gIdx = 0;
    for (var gi = 0; gi < groups.length; gi++) {
      for (var gj = 0; gj < groups[gi].items.length; gj++) {
        if (groups[gi].items[gj].index === idx) { gIdx = gi; }
      }
    }
    var gr = groups[gIdx] || { items: [{ station: st, index: idx }], title: st.exerciseName };
    var members = gr.items;
    var isCircuit = members.length > 1;
    var rounds = members.reduce(function (n, m) {
      var e = pos.perStation[m.index];
      return Math.max(n, e ? e.target : 0);
    }, 0);

    var nxEarly = nextCircuitStep(stations, pos);

    var rows = '';
    for (var i = 1; i <= rounds; i++) {
      if (isCircuit) {
        rows += '<div class="bf-round-head"><h3>Round ' + i + ' of ' + rounds + '</h3></div>';
      }
      for (var mi = 0; mi < members.length; mi++) {
        rows += setRowFor(members[mi].station, pos.perStation[members[mi].index], i, isCircuit);
      }
    }

    function setRowFor(mSt, mHere, i, showName) {
      if (!mHere || i > mHere.target) return '';
      /* Resolved per station, because it reads that movement's own history. */
      var previously = lastTimeFor(logged, occurrence, selectedVariant(occurrence, mSt).name);
      var prior = mHere.logged[i];
      /* The highlighted row is the next thing to DO, which inside a circuit is
         a different exercise from the one whose set you just saved. Using the
         station's own nextSet would have highlighted a row in every exercise
         at once. */
      var isNext = !pos.sessionComplete && !prior && !!nxEarly &&
        nxEarly.station.id === mSt.id && nxEarly.round === i;
      var setWord = showName ? '' : 'Set ';
      var st = mSt, here = mHere;
      /* Only where it is still a decision. Once the set is logged the member
         has already chosen their weight, and last week's number beside this
         week's is just noise on a row they have finished with. */
      var lastTime = prior ? '' : lastTimeLabel(previously, i);
      /* THE PRESCRIPTION TRAVELS WITH THE ROW.
         Grouping the circuit replaced the set number with the exercise name,
         and the header tag with a round count — between them the written
         prescription, 8 to 15, stopped appearing anywhere on the screen. A
         member cannot do a set they have not been told the size of. */
      var presc = showName && !prior
        ? repsLabel(st) + (unitFor(st, occurrence) === 'seconds' ? ' sec' :
            (/per side|second|plank/i.test(repsLabel(st)) ? '' : ' reps'))
        : '';
      /* HOW TO DO IT, ON THE ROW THAT ASKS FOR IT.
         The setup steps are approved, coach-reviewed content that the member
         is already entitled to, and until now the only way to them was one
         "Help with this exercise" link at the very bottom of the screen, under
         a heading reading "Need a change?" and scoped to a single station
         while a circuit lists four. A member mid-round could not reach the
         instruction for the exercise actually in front of them.

         Becca, on 2026-10-27, having finished a whole session: "I found myself
         not knowing what some of these were or how to do them." Both exercises
         she named — Wall hip hinge, Prone pull-back — had four approved steps
         each, written and reviewed by Todd on 2026-10-04. Nothing was missing
         except a way to read it.

         A native <details> rather than a control we wire up: it opens without
         JavaScript, it is a disclosure to a screen reader without any ARIA of
         ours, and it costs no height until someone asks. It carries the <h3>
         so the row's heading, and everything styled or asserted against it,
         is unchanged.

         EMPTY-WRAPPER GUARD. No steps, no disclosure — not an empty one. A
         station whose content is not approved yet has nothing to show, and a
         control that opens onto nothing is worse than no control. */
      var heading = '<h3>' + (showName ? esc(mSt.exerciseName) : setWord + i) + '</h3>';
      var title = showName && mSt.setupSteps && mSt.setupSteps.length
        ? '<details class="bf-howto"><summary>' + heading +
            '<span class="bf-howto-cue">How do I do this?</span></summary>' +
            '<ol class="bf-howto-steps">' +
            mSt.setupSteps.map(function (s) { return '<li>' + esc(s) + '</li>'; }).join('') +
            '</ol></details>'
        : heading;
      return '<div class="bf-row' + (isNext ? ' bf-row-next' : '') + '"><div>' + title +
        (prior
          ? '<p>' + loggedLabel(prior) + '</p>'
          : '<p>' + (presc ? esc(presc) + ' \u00b7 ' : '') +
            (isNext ? 'Next up' : 'Not logged') + '</p>') +
        (lastTime ? '<p class="bf-lasttime">' + esc(lastTime) + '</p>' : '') + '</div>' +
        '<div class="bf-form" style="grid-auto-flow:column;gap:8px">' +
          (takesWeight(st)
            ? '<input inputmode="numeric" placeholder="Weight" data-log-weight="' + i + '" style="width:92px">'
            : '') +
          '<input inputmode="numeric" placeholder="' + esc(unitFor(st, occurrence) === 'seconds' ? 'Seconds' : repsHint(st)) +
            '" data-log-reps="' + i + '" data-unit="' + esc(unitFor(st, occurrence)) + '" style="width:110px">' +
          /* A SAVED SET IS CORRECTED, NOT SAVED AGAIN.
             The row said "Logged: 8 reps" beside a button still labelled
             Save, which reads as an unfinished action and invites logging the
             same set twice. The write path is the same one either way — the
             upsert already replaces the row — so only the word changes, and
             with it what the member thinks pressing it will do. */
          '<button class="bf-btn small ' + (prior ? 'ghost' : 'cyan') + '" data-log-set="' + i + '"' +
            ' data-station="' + esc(st.id) + '"' +
            (prior ? ' data-correcting="1"' : '') +
            ' data-variant="' + esc(selectedVariant(occurrence, st).name) + '"' +
            ' data-rest="' + esc(restForLogging(stations, st) || '') + '">' +
            (prior ? 'Edit' : 'Save') + '</button>' +
        '</div>' +
        /* THE ROW ANSWERS WHERE IT WAS TAPPED.
           The panel-level message sits below every row and the rest timer.
           Measured on an iPhone 17 Pro Max against the live site: a Save on
           round 1 put its reply about 1,100px down the page, so the member saw
           the cursor return to the box and nothing else. A refusal nobody can
           read is a refusal with no reason attached, which is exactly how
           "logging is required" got believed. Empty until something is said,
           so it costs no height until it has to. */
        '<p class="bf-note" data-log-msg-row role="status" aria-live="polite"></p>' +
        '</div>';
    }

    /* Moving between exercises is what makes the session completable at all.
       These set the station explicitly, so a member can go back to correct a
       number without the screen dragging them forward again. */
    /* The circuit-order step is the PRIMARY control; the two beside it stay so
       a member can go back and correct a number without being dragged forward
       again, which was the reason they exist. */
    var nx = nxEarly;
    /* WITHIN A CIRCUIT THERE IS NOWHERE TO GO. Every round of every exercise in
       this block is already on the screen, so a "next" button pointing at the
       partner exercise would be asking the member to navigate to something
       they can already see — which is the flipping that was reported. A jump
       is offered only when the next thing is in ANOTHER block, and it is
       labelled with that block's name rather than an exercise's. */
    var inThisGroup = function (i) {
      for (var k = 0; k < members.length; k++) if (members[k].index === i) return true;
      return false;
    };
    var groupOfIndex = function (i) {
      for (var g = 0; g < groups.length; g++) {
        for (var k = 0; k < groups[g].items.length; k++) if (groups[g].items[k].index === i) return g;
      }
      return -1;
    };
    /* FORWARD MEANS FORWARD.
       nx is the first unfinished set in the LOG, which is not always ahead of
       where the member is standing. Walked on a phone: on Core, with circuit 2
       skipped, the loudest control on the screen was a pink "NEXT: Circuit 2
       \u2192" sending them BACKWARDS, beside a ghost "\u2190 Circuit 2" to the
       same place. That is the control a tester asked to have replaced by Core
       naming itself last, and it had survived the change that was supposed to
       answer her.

       A prominent jump is offered only when the unfinished work is in a LATER
       block. Behind them it is stated, not urged \u2014 going back is their
       call, and the \u2190 control beside it already does it. */
    var nxGroupIdx = nx ? groupOfIndex(nx.index) : -1;
    var stepIsForward = !!(nx && !inThisGroup(nx.index) && nxGroupIdx > gIdx);
    var nextLabel = nx
      ? (inThisGroup(nx.index)
          ? (isCircuit
              ? esc(nx.station.exerciseName) + ', round ' + nx.round + ' is next'
              : 'Set ' + nx.round + ' is next')
          : (stepIsForward ? 'Next: ' : '') +
            esc((groups[nxGroupIdx] || {}).title || nx.station.exerciseName) +
            (stepIsForward ? '' : ' still has sets to log'))
      : '';
    var circuitStep = stepIsForward
      ? '<div class="bf-actions"><button class="bf-btn primary" data-station-step="' + nx.index +
        '" data-occurrence="' + esc(occurrence || '') + '">' + nextLabel + ' \u2192</button></div>'
      : (nx ? '<p class="bf-sub">' + nextLabel + '.</p>' : '');

    /* Back and forward move between BLOCKS too, for the same reason. */
    var prevG = groups[gIdx - 1], nextG = groups[gIdx + 1];
    /* SAY WHEN THIS IS THE LAST PART.
       Finishing a circuit re-renders onto the first unfinished station, so a
       member never sees the "Next: Core" button — they simply arrive, and the
       only control on the screen points BACK to the circuit they just
       finished. It reads as though the app wants them to return. Reported as:
       instead of the button going back to the previous circuit, it should be
       CORE so you know that is the last of the session. */
    var isLastGroup = !nextG;
    var groupDone = members.every(function (m) {
      var e = pos.perStation[m.index];
      return e && e.complete;
    });
    var lastNote = isLastGroup
      ? '<p class="bf-sub"><strong>' + esc(gr.title) + '</strong> \u2014 the last part of this session.</p>'
      : '';
    var steps = lastNote + circuitStep + '<div class="bf-actions">' +
      (prevG
        ? '<button class="bf-btn ghost small" data-station-step="' + prevG.items[0].index +
          '" data-occurrence="' + esc(occurrence || '') + '">\u2190 ' + esc(prevG.title) + '</button>'
        : '') +
      /* Dropped when the control above already leads here: finishing circuit 1
         showed a pink "NEXT: Circuit 2" directly over a cyan "Circuit 2". One
         destination, one way to reach it. */
      (nextG && !(stepIsForward && nxGroupIdx === gIdx + 1)
        ? '<button class="bf-btn ' + (groupDone ? 'cyan' : 'ghost') + ' small" data-station-step="' +
          nextG.items[0].index + '" data-occurrence="' + esc(occurrence || '') + '">' +
          esc(nextG.title) + ' \u2192</button>'
        : '') +
      '</div>';

    var doneStations = pos.perStation.filter(function (x) { return x.complete; }).length;

    /* WHERE THEY ARE IN THE SESSION AS WRITTEN. "Exercise 1 of 5" describes a
       list; this program is two circuits and a core set, and a member
       alternating between two movements needs to be told that rather than
       left to infer it from the order. */
    var blk = blockOf(guidance, st);
    /* The rest a round prescribes when there is a block, the station's own
       otherwise. A program with no blocks must not lose its rest value. */
    var restVal = (blk && blk.restAfterRound) || st.rest || '';
    var group = partnersIn(stations, st);
    /* NAME THE SCREEN AFTER WHAT IS ON IT. It was titled with a single
       exercise while the member was being moved between several, which is
       what made the session feel unorganised. A circuit is titled as the
       circuit; a lone exercise is still titled as itself. */
    var screenTitle = isCircuit ? gr.title : st.exerciseName;
    var where = isCircuit
      ? rounds + ' rounds' + (blk && blk.restAfterRound ? ', resting ' + esc(blk.restAfterRound) + ' after each' : '')
      : (blk ? blk.title : 'Exercise ' + (idx + 1) + ' of ' + stations.length);
    var alternate = group.length > 1
      ? '<p class="bf-sub">Alternate ' +
        group.map(function (x) { return esc(x.exerciseName); }).join(', then ') +
        '. Rest after the pair. Every round is on this screen \u2014 you only ' +
        'move on when the circuit is done.</p>'
      : '';

    return head('GUIDED WORKOUT', screenTitle,
      where + '. Your log saves as you go.',
      tag(isCircuit ? rounds + ' rounds' : st.sets + ' \u00d7 ' + repsLabel(st), 'cyan')) +
      '<div class="bf-split"><div class="bf-stack">' +
        (anyLogged ? '' : warmupPanel(guidance)) +
        howHardPanel(guidance) +
        /* SAYING THE RULE THAT WAS NEVER THERE IS NOT A RULE.
           Nothing gates completion or credit on a logged set — /completions
           does not look at the log — but the only loud control on this screen
           is Save, and Save refuses an empty box. A tester concluded logging
           was mandatory and reported it as a bug. It sits above the rows
           because the moment it is needed is before the first tap, not after
           the member has already decided they are stuck. */
        panel('Log your sets', alternate +
          '<p class="bf-sub">Logging is optional \u2014 it is how you see progress ' +
          'over time, not how the session completes.</p>' +
          /* THE REST TIMER, ABOVE THE ROUNDS AND NEVER APPEARING.
             Asked for by a tester: "timer for rest does look like it needs to
             be moved up if possible". It was below the rounds because the
             region carried `hidden` until a timer ran, and revealing it above
             the rows would have pushed every row down the screen mid-set —
             the jump the save-scroll work exists to prevent.

             So it stops appearing. One region, always rendered, holding two
             states: the start control when idle, the countdown when running.
             The handler swaps the children and never touches the region, and
             the CSS pins a min-height across both, so starting a rest changes
             what the box says and not where anything below it sits. */
          (restVal
            ? '<div class="bf-rest" data-rest-region>' +
                '<div data-rest-idle>' +
                  '<button class="bf-btn small ghost" data-rest-start="' + esc(restVal) + '">' +
                  'Start rest (' + esc(restVal) + ')</button>' +
                '</div>' +
                '<div class="bf-rest-running" data-rest-running hidden>' +
                  '<div><h3 data-rest-clock>0:00</h3>' +
                  '<p class="bf-sub" data-rest-label>Rest</p></div>' +
                  '<div class="bf-actions">' +
                    '<button class="bf-btn small ghost" data-rest-add="30">+30s</button>' +
                    '<button class="bf-btn small cyan" data-rest-skip>Skip rest</button>' +
                  '</div>' +
                '</div>' +
              '</div>'
            : '') + rows +
          '<p class="bf-note" data-log-msg role="status" aria-live="polite"></p>' + steps) +
        variantPanel(occurrence, st) +
      '</div><div class="bf-stack">' +
        /* Built here rather than through row() so each line carries a hook the
           save handler can update in place. Without them the totals only moved
           on reload, which is how a member ended up unable to tell what they
           had already logged. */
        panel('This session',
          pos.perStation.map(function (x, n) {
            return '<div class="bf-row" data-session-row="' + esc(x.station.id) + '">' +
              '<div><h3>' + esc(x.station.exerciseName) + '</h3>' +
              '<p data-session-count>' + x.doneCount + ' of ' + x.target + ' sets</p></div>' +
              '<div><span class="bf-tag ' + (x.complete ? 'cyan' : (n === idx ? 'yellow' : '')) +
              '" data-session-state>' + (x.complete ? 'Done' : (n === idx ? 'Here' : 'To do')) +
              '</span></div></div>';
          }).join('') + reviewLine(stations)) +
        panel('When you are done',
          '<p class="bf-sub">' + (pos.sessionComplete
            ? 'Every set is logged. Finishing reports the session to your record.'
            : doneStations + ' of ' + stations.length + ' exercises complete. Finishing reports the session to your record.') + '</p>' +
          /* THE WORKOUT THIS FINISHES, carried on the button. Sent with the
             report so a retry that arrives late cannot be answered with
             whatever session is current by then — it names the one occurrence
             it was always for. */
          '<div class="bf-actions"><button class="bf-btn primary" data-finish' +
            (occurrence ? ' data-occurrence="' + esc(occurrence) + '"' : '') +
            '>Finish session</button></div>' +
          '<p class="bf-note" data-finish-msg role="status" aria-live="polite"></p>') +
        exerciseGuidance(st, !isCircuit) +
        safetyPanel(guidance) +
        panel('Need a change?',
          /* Help is never gated by experience level. Someone who trains four
             times a week can still meet a machine they have not used, and
             making them restate themselves as a beginner to see the setup
             would be a strange price for one question. */
          '<div class="bf-actions">' +
          btn('Help with this exercise', 'player/' + encodeURIComponent(st.id), 'ghost small') +
          btn('See alternatives', 'swap/' + encodeURIComponent(st.id), 'ghost small') + '</div>') +
      '</div></div>';
  });

  A.register('complete', async function () {
    var prog = await load('progress', '/progress', true);
    var week = prog && prog.week;
    /* A week only exists now when the member accepted a schedule, so its
       presence IS the acceptance check. The profile is still read for the work
       actually done, which is what the screen falls back to. */
    var profile = await load('profile', '/profile', true);
    var eng = (profile && profile.engagement) || {};
    return head('SESSION COMPLETE', 'That’s the session. Nice work showing up.',
      'Consistency is the whole mechanism. One session is one step.',
      '', 'Nice work') +
      '<div class="bf-split">' +
        /* "1 of 3" WAS NOT THIS MEMBER'S NUMBER.
           plannedSessionsFor falls back to a configured default when no
           schedule has been accepted, so a member who never agreed to three
           sessions a week was shown a weekly commitment and told they were one
           of three through it. The window keeps its own number for credit
           maths, which is already pinned and is not touched here; what changes
           is that the SCREEN only states a target the member actually accepted.
           Without one it reports the work done and nothing more. */
        /* NO ACCEPTED SCHEDULE MEANS NO WEEK AT ALL NOW.
           The backend used to open a window against a default of three, so
           there was always a `week` to render. It no longer invents one, which
           is right — but "Progress appears once a week is running" would then
           be the whole answer to someone who had just trained, and it reads
           like their session did not count. It did: the workout is recorded and
           any daily credit is awarded. What is missing is a target, so that is
           what the screen says, with the way to set one. */
        panel('Where that leaves you', week
          ? row('This week', week.completedSessions + ' of ' + week.requiredSessions)
            + row('Remaining', String(week.remainingSessions))
            + row('Reflection', week.hasReflection ? 'Recorded' : 'Open', week.hasReflection ? '' : btn('Reflect', 'reflection', 'small cyan'))
          : row('Workouts completed', String(eng.completionsAllTime || 0))
            + row('Last trained', eng.lastTrainedOn || 'Today')
            + row('Weekly target', 'Not set',
                  btn('Choose my days', 'week', 'small cyan'))
            + note('Your workouts and credits are recorded. A weekly target starts ' +
                   'when you accept a schedule \u2014 nothing is counted against you until then.', false)) +
        '<div class="bf-stack">' + panel('', '<div class="bf-actions">' +
          btn('My progress', 'progress', 'primary') + btn('Back to today', 'home', 'ghost') + '</div>') + '</div>' +
      '</div>';
  });

  /* ================= ACCOUNTABILITY =================================== */

  A.register('progress', async function () {
    var prog = await load('progress', '/progress', true);
    if (!prog) return head('PROGRESS', 'Progress is unavailable.', '') + panel('', empty('Try again shortly.'));
    var week = prog.week;
    /* A WEEK IS A TARGET, NOT A RECORD OF TRAINING.
       With no accepted schedule there is no consistency window, and this panel
       said "No week running. Your first session starts one." to a member whose
       credits screen showed two reported workouts and whose completion screen
       named the date of the last one. The sessions are real and recorded; what
       does not exist is a target to measure them against. The completion
       screen already says exactly that — this one contradicted it. */
    var profile = await load('profile', '/profile', true);
    var eng = (profile && profile.engagement) || {};
    return head('YOUR PROGRESS', 'What you have actually done.',
      'Counted from workouts you reported — not from time spent on this page.',
      accessLine(prog.access), 'actually done') +
      '<div class="bf-split"><div class="bf-stack">' +
        panel('This week', week
          ? row('Sessions', week.completedSessions + ' of ' + week.requiredSessions)
            + row('Remaining', String(week.remainingSessions))
            + row('Weekly reflection', week.hasReflection ? 'Recorded' : 'Not yet',
                week.hasReflection ? '' : btn('Reflect now', 'reflection', 'small cyan'))
          : row('Workouts completed', String(eng.completionsAllTime || 0))
            + row('Last trained', eng.lastTrainedOn || 'Not yet')
            + row('Weekly target', 'Not set', btn('Choose my days', 'week', 'small cyan'))
            + note('Your workouts and credits are recorded. A weekly target starts '
                 + 'when you accept a schedule.', false)) +
        /* The ladder is the server's. It used to be written here as [4, 8, 12],
           a second copy of the constant that decides which entitlement is
           issued — so the screen could promise a milestone the rule would not
           grant. The fallback covers an API that has not shipped the field
           yet and is not a place to keep it up to date. */
        panel('Consistency journey',
          ((prog.milestones && prog.milestones.length) ? prog.milestones : [4, 8, 12]).map(function (m) {
            var done = prog.bankedWeeks >= m;
            /* "Earned" sat next to a panel headed "Earned gear" and read as
               gear for milestones that ship nothing. The row already says
               Reached; the tag now agrees with it. */
            return row(m + ' consistent weeks',
              done ? 'Reached' : (m - prog.bankedWeeks) + ' to go',
              tag(done ? 'Reached' : 'Locked', done ? 'cyan' : ''));
          }).join('')) +
      '</div><div class="bf-stack">' +
        panel('Banked weeks', '<p style="font-family:var(--bf-display,Anton);font-size:52px;margin:0">' +
          esc(prog.bankedWeeks) + '</p><p class="bf-sub">A week banks with the target met and a reflection recorded.</p>') +
        (prog.preview ? panel('', note('Preview account — milestones are not issued and nothing ships.')) : '') +
      '</div></div>';
  });

  /* GEAR IS ONLY THE ONE MILESTONE THAT SHIPS.
     Every banked-week threshold writes an entitlement, but only one of them
     has anything to send. The API marks each row with `ships`, derived from
     the same rule that issues the fulfilment, so this never needs to know the
     rule's name. Older responses have no flag; treating those as gear is the
     behaviour that was wrong, so an unmarked row is not gear. */
  function gearRewards(prog) {
    return ((prog && prog.rewards) || []).filter(function (r) { return r && r.ships; });
  }

  A.register('credits', async function () {
    var c = await api('/credits');
    var prog = await load('progress', '/progress', true);
    if (!c.ok) return head('CREDITS', 'Credits are unavailable.', '') + panel('', empty('Try again shortly.'));
    var entries = c.data.entries || [];
    /* CREDITS EARN THE GEAR, AND THIS SCREEN USED TO DENY IT.
       A tester asked twice for "an active reward bar on how close they are to
       a reward". I answered the first time by writing copy saying credits buy
       nothing and that gear comes from banked weeks — read out of
       issueMilestones in consistency.ts, a function that is exported and never
       called from anywhere, so no entitlement has ever been issued by any
       rule. Harrison: "credits do earn gear - when you get credits, it moves
       bar, not banked weeks", and "banked weeks doesnt make sense".

       So the bar runs on credits against creditGoal, which /progress reads
       from the same constant used by the rule that writes the entitlement, so
       the threshold is not restated here and cannot drift from it. Weeks are
       still counted, as a record of consistency, and no longer carry a reward
       claim on this screen. */
    var week = prog && prog.week;
    var canEarn = !!(prog && prog.access && prog.access.canEarn);
    var tiers = (prog && prog.rewardTiers) || [];
    var banked = prog && prog.bankedWeeks != null ? prog.bankedWeeks : null;

    var weekRule = week
      ? row('This week',
          week.completedSessions + ' of ' + week.requiredSessions + ' sessions · ' +
          (week.hasReflection ? 'reflection recorded' : 'reflection not yet'))
      : row('This week', 'Your first week begins with your first session.');

    /* THE BAR SHE CAN SEE.
       The first version was a row of discrete blocks, one per week, and twice
       she reported "still dont see a active reward bar on how close they are
       to a reward" while looking straight at it. The example she sent is a
       continuous track with a marker carrying the number and a scale
       underneath, which is what this draws.

       One thing from that example is deliberately not copied: it pictures the
       prize. We name nothing, because no reward has been agreed — a bar
       pointing at an unnamed thing is honest, one pointing at a prize nobody
       has committed to is not. */
    /* THE BAR RUNS BETWEEN TIERS NOW.
       It used to run 0 to 100 against a single unnamed prize, so it sat full
       and finished after one reward. The server sends `rewardBar` as
       {from, to, next}: the tier just earned, the one being worked towards,
       and its name. The thresholds are the server's and are never restated
       here - a second copy is a number that can disagree with the one that
       issues the entitlement. */
    function rewardBar(have, range, announced) {
      if (!range || !range.to) return '';
      var goal = range.to, from = range.from || 0;
      var n = Math.max(0, have || 0);
      var span = Math.max(1, goal - from);
      var pct = Math.round((Math.min(Math.max(n - from, 0), span) / span) * 1000) / 10;
      var marks = [0, 0.25, 0.5, 0.75, 1].map(function (f) { return Math.round(from + span * f); });
      var ticks = marks.map(function (m, i) {
        return '<span class="' + (i === marks.length - 1 ? 'bf-rewardbar-gear' : '') + '">' +
          m + '</span>';
      }).join('');
      return '<p class="bf-meter-cap">' + n + ' of ' + goal + ' credits</p>' +
        '<div class="bf-rewardbar" role="progressbar" aria-valuemin="0" aria-valuemax="' + goal +
        '" aria-valuenow="' + n + '" aria-label="Credits towards gear">' +
          '<div class="bf-rewardbar-track">' +
            '<div class="bf-rewardbar-fill" style="width:' + pct + '%"></div>' +
            /* --bf-p carries the same percentage to the transform, which uses it
               to keep the pill on the rail at both ends. See bf-rewardbar-marker. */
            '<div class="bf-rewardbar-marker" style="left:' + pct + '%;--bf-p:' + pct + '">' +
              n + '</div>' +
          '</div>' +
          '<div class="bf-rewardbar-scale" style="grid-template-columns:repeat(' +
            marks.length + ',1fr)">' + ticks + '</div>' +
        '</div>' +
        /* The undecided line disappears once a reward is confirmed. Left
           unconditional it told a member the gear was not announced while the
           panel below offered them a claim on it. */
        '<p class="bf-meter-cap">' + esc(range.next.name) + ' at ' + goal +
        ' credits — ' + Math.max(0, goal - n) + ' to go.' +
        (announced ? '' : ' Claiming opens when shipping does; what you earn ' +
         'is kept either way.') + '</p>';
    }

    return head('CREDITS & REWARDS', 'Earn as you train.',
      'Every workout you report earns credits. Credits earn the gear.',
      tag(c.data.balance + ' credits', 'cyan'), 'train') +
      '<div class="bf-split"><div class="bf-stack">' +
        panel('Your credits',
          row('What they are',
            'A record that you showed up, and what earns the gear. They are ' +
            'not money and cannot be spent anywhere else.') +
          row('Report a workout', '+10, once a day') +
          row('Weekly reflection', '+5, once a week') +
          row('Share BullFit', '+5, once a week')) +
        /* KOURTNEY'S IDEA, AND HONEST ABOUT WHAT IT IS.
           Nothing checks Instagram. Saying "we will verify" would be a lie and
           saying nothing invites the member to assume it — so the panel states
           it plainly, the way the rest of this app states what it does not
           know. The once-a-week cap is the control, which is the same answer
           this product already gives for a reported workout. */
        panel('Share BullFit', (function () {
          var done = week && week.hasSharedPost;
          if (!canEarn) return note('Claiming this needs an active membership.', true);
          if (!week) return note('This starts once your first week is running.');
          if (done) return note('Claimed for this week. It comes round again next week.');
          return '<p class="bf-sub">Post to your story and tag BullFit, then claim it ' +
            'here. We do not check \u2014 you are telling us, and it is once a week ' +
            'either way.</p>' +
            '<div class="bf-actions"><button class="bf-btn cyan" data-shared>' +
            'I shared BullFit</button></div>' +
            '<p class="bf-note" data-shared-msg role="status" aria-live="polite"></p>';
        })()) +
        panel('Recent activity', entries.length
          ? entries.slice(0, 12).map(function (e) {
              return row(e.kind === 'reflection' ? 'Weekly reflection' : 'Workout reported',
                String(e.local_day || '').slice(0, 10), tag('+' + e.amount, 'cyan'));
            }).join('')
          : empty('Nothing yet. Reporting a workout is what earns credits.')) +
      '</div><div class="bf-stack">' +
        /* The bar is its own panel at the top of the column, because it is the
           one thing she looked for and did not find. */
        panel('Towards the gear',
          rewardBar(c.data.balance, prog && prog.rewardBar,
                    prog && prog.rewardFulfilmentEnabled) ||
            empty(tiers.length
              ? 'Every piece of gear is earned. Nothing left to work towards.'
              : 'Your progress towards the gear appears here.')) +
        /* WHAT CAN BE EARNED, NAMED. The bar pointed at a blank, which is what
           made two and a half weeks feel like forever. */
        /* NOT EVERY RUNG IS GEAR ANY MORE. This panel was called "The gear"
           and every row was an object in the post. The first rung of the
           2026-10-23 ladder unlocks the member discount and sends nothing, so
           a row reading "Earned" under a heading promising gear would have a
           member waiting on a parcel that was never coming. The server marks
           each tier with `ships`; this says which kind it is rather than
           restating the ladder. */
        panel('What you can earn', tiers.length
          ? tiers.map(function (t) {
              var kind = t.ships === false ? ' · unlocks, nothing posted' : '';
              return row(t.name, t.credits + ' credits' + kind,
                tag(t.earned ? 'Earned' : 'Locked', t.earned ? 'cyan' : ''));
            }).join('')
          : empty('What you can earn appears here.')) +
        /* Weeks are a record of turning up. THEY NO LONGER CLAIM A REWARD:
           "Next milestone · 3 to go" sat here pointing at a ladder whose rule
           issues nothing, which is a promise this screen cannot keep. */
        panel('Your weeks',
          row('How a week banks',
            'Complete your planned sessions and record your reflection. Your ' +
            'week runs seven days from the day you started, not Monday to Sunday.') +
          row('A missed week', 'Costs you nothing. Banked weeks only ever add up.') +
          weekRule +
          (banked == null
            ? empty('Your banked weeks appear here once your first week closes.')
            : row('Banked weeks', String(banked), tag(String(banked), 'cyan')))) +
        panel('Earned gear', gearRewards(prog).length
          ? gearRewards(prog).map(function (r) {
              return row('Earned at ' + r.earned_at_banked_weeks + ' credits',
                prog.rewardFulfilmentEnabled ? 'Ready to claim' : 'Claiming opens when the reward is confirmed',
                btn('Claim', 'redeem', 'small ghost'));
            }).join('')
          /* NOT NAMING A REWARD THAT HAS NOT BEEN DECIDED.
             rewardFulfilmentEnabled stays off until a reward product and its
             terms are approved, so there is nothing to name. Saying so is
             weaker than naming a prize and is the only honest option until one
             exists — and it still promises that what is earned now is kept. */
          : empty(prog && prog.rewardFulfilmentEnabled
              ? 'Reach the goal and it appears here.'
              : 'The gear itself is not announced yet. We will not name one ' +
                'before the terms are settled. Anything you earn before then ' +
                'is recorded and will be here to claim.')) +
      '</div></div>';
  });

  A.register('redeem', async function () {
    var prog = await load('progress', '/progress', true);
    var rewards = gearRewards(prog);
    return head('CLAIM EARNED GEAR', 'What you have earned.',
      'Earned gear stays yours. Cancelling does not remove it.', '', 'you have earned') +
      panel('Your rewards', rewards.length
        ? rewards.map(function (r) {
            /* Named the same way the credits screen names it. The column is
               earned_at_banked_weeks for historical reasons; the only rule that
               writes it stores a credit count. */
            return row('Earned at ' + r.earned_at_banked_weeks + ' credits',
              prog.rewardFulfilmentEnabled ? 'Ready to claim' : 'Not claimable yet',
              prog.rewardFulfilmentEnabled
                ? '<button class="bf-btn small cyan" disabled>Claim</button>'
                : tag('Awaiting product', 'yellow'));
          }).join('')
        : empty('Nothing earned yet.')) +
      (prog && !prog.rewardFulfilmentEnabled
        ? note('Physical fulfilment is switched off until a reward product is approved. Your entitlement is recorded either way.') : '');
  });

  A.register('reflection', async function () {
    var prog = await load('progress', '/progress', true);
    var week = prog && prog.week;
    var done = week && week.hasReflection;
    var canEarn = prog && prog.access && prog.access.canEarn;
    return head('WEEKLY REFLECTION', 'How did this week go?',
      'An honest reflection counts even after a hard week — it is part of banking the week.', '', 'this week') +
      '<div class="bf-split">' +
        panel('Record this week', done
          ? note('Reflection recorded for this week.')
          : !canEarn
            ? note('Recording a week needs an active membership.', true)
            /* "Nothing to write unless you want to" offered a note this screen
               cannot take: there is no field, and POST /reflections sends an
               empty body. A tester read it the only way it reads — "there is
               no where to write a note if thats the goal of that spot". Say
               what the button does instead of hinting at something more. */
            : '<p class="bf-sub">One tap records the week. There is nothing to ' +
              'write \u2014 turning up and marking it is the whole action.</p>' +
              '<div class="bf-actions"><button class="bf-btn cyan" data-reflect>Record this week</button></div>' +
              '<p class="bf-note" data-reflect-msg role="status" aria-live="polite"></p>') +
        panel('Why it matters', row('Banking a week', 'Needs the session target met and a reflection recorded.') +
          row('Missing a week', 'Removes nothing you have already earned.')) +
      '</div>';
  });

  A.register('sms', async function () {
    var sms = await load('sms', '/sms', true);
    if (!sms) return head('TEXT CHECK-INS', 'Unavailable.', '') + panel('', empty('Try again shortly.'));
    var state = !sms.available
      ? note('Text check-ins are not switched on yet. You can set a preference now and it will apply when they are.', true)
      : !sms.consent
        ? '<p class="bf-sub">Get a text on your training days asking if you trained.</p>'
        : !sms.consent.verified
          /* TWO FLAGS, AND ONLY ONE OF THEM WAS BEING READ.
             `available` means the subsystem is reachable; `configured` means a
             provider is actually set up. The consent route is guarded on
             `available` alone, so with no provider it records consent, stores
             a code and sends nothing — the API says so, returning
             verificationSent: false with a reason. This line asserted a text
             was on its way regardless, and a tester waited for one that could
             never arrive: "no code was sent to phone number". */
          ? note(sms.configured
              ? 'We sent a code to the number ending ' + sms.consent.phoneLast4 + '.'
              : 'No code was sent: texting is not switched on yet. Your number ' +
                'is saved and ends ' + sms.consent.phoneLast4 + '.', !sms.configured)
          : '<p class="bf-sub">Texting the number ending ' + esc(sms.consent.phoneLast4) + '.</p>';

    var form = (sms.consent && sms.consent.verified)
      ? '<div class="bf-form"><label>Send at<input type="time" value="' +
          esc((sms.preference && sms.preference.send_local_time) || '18:00') + '" data-sms-time></label></div>' +
        '<div class="bf-actions"><button class="bf-btn cyan" data-sms-save>Save</button>' +
        '<button class="bf-btn ghost" data-sms-stop>Stop texts</button></div>'
      : (sms.consent && !sms.consent.verified)
        /* A WAY OUT OF WAITING. This state offered one control — enter the
           code — so a member whose code never came had nowhere to go. The
           opt-out route already clears the consent and returns them to the
           number form, which is also how they retry with a different number;
           it was only ever offered once a number was verified. */
        ? (sms.configured
            ? '<div class="bf-form"><label>Six-digit code<input inputmode="numeric" maxlength="6" data-sms-code></label></div>' +
              '<div class="bf-actions"><button class="bf-btn cyan" data-sms-confirm>Confirm</button>' +
              '<button class="bf-btn ghost" data-sms-stop>Use a different number</button></div>'
            : '<div class="bf-actions"><button class="bf-btn ghost" data-sms-stop>' +
              'Use a different number</button></div>')
        : '<p class="bf-sub" style="font-size:12px">' + esc(sms.disclosure ? sms.disclosure.text : '') + '</p>' +
          '<label style="display:flex;gap:8px;align-items:flex-start;margin:10px 0"><input type="checkbox" data-sms-agree>' +
          '<span>Yes, text me on my training days.</span></label>' +
          '<div class="bf-form"><label>Mobile number<input type="tel" inputmode="tel" placeholder="(512) 555-0147" data-sms-phone></label></div>' +
          '<div class="bf-actions"><button class="bf-btn cyan" data-sms-submit' + (sms.available ? '' : ' disabled') + '>Send me a code</button></div>';

    return head('TEXT CHECK-INS', 'Reply and it is logged.',
      'One text on your training days. Reply to log the workout — nothing else to do.',
      tag(sms.configured ? 'Sending on' : 'Sending off', sms.configured ? 'cyan' : 'yellow'),
      'it is logged') +
      '<div class="bf-split">' +
        panel('Your preference', state + form + '<p class="bf-note" data-sms-msg role="status" aria-live="polite"></p>') +
        panel('What a check-in looks like', '<div class="bf-actions">' + btn('See an example', 'smsdemo', 'ghost small') + '</div>' +
          note('Consent is separate from your email preferences and is never assumed from them.')) +
      '</div>';
  });

  A.register('smsdemo', async function () {
    return head('SMS CHECK-IN EXAMPLE', 'This is the whole exchange.',
      'No app to open. Reply and your record updates.', '', 'whole exchange') +
      '<div class="bf-split">' +
        panel('The text you get',
          '<div class="bf-panel" style="background:var(--bf-bg)"><p style="margin:0">BullFit: did you train today? Reply YES to log it. STOP to stop.</p></div>' +
          '<div class="bf-actions">' + tag('Sent on your training days', 'cyan') + '</div>') +
        panel('What happens next',
          row('You reply', 'The reply is matched to the session it asked about.') +
          row('After midnight', 'Still counts for the day it asked about, not the day you replied.') +
          row('Already logged in the app', 'Counted once. Never twice.') +
          row('You reply NO', 'Acknowledged. Nothing you have earned is removed.')) +
      '</div>';
  });

})();
